import { useEffect, useMemo, useState, useRef } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import api, { money } from "../../lib/api";
import { errorToastFromAxios } from "../../lib/supportToast";
import {
  SCORE_SIGNAL_LABELS as V3_SIGNAL_LABEL,
  SCORE_SIGNAL_FALLBACK_WEIGHTS_PCT as V3_SIGNAL_WEIGHT,
  SCORE_WEIGHTED_KEYS,
  SCORE_TIER_EXPLAINERS,
  SCORE_DISCLAIMER,
  normalizeRating,
} from "../../lib/scoreLabels";
import { PageHeader } from "../../components/PortalShell";
import SearchAndFilter from "../../components/SearchAndFilter";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "../../components/ui/sheet";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "../../components/ui/popover";
import { toast } from "sonner";
import {
  Plus, Trash2, Pencil, Mail, ShieldCheck, AlertCircle,
  Search, ChevronRight, Activity, ShoppingBag, Ban,
  TrendingUp, X, Receipt, Sparkles, AlertTriangle, RefreshCw,
  MoreHorizontal, MessageCircle, ChevronDown, Check, Users,
  Clock, Inbox, Filter, Send, DollarSign, Tag, Shield,
  Download, Info, Truck, Building2, MapPin, Calendar,
  Globe, Phone, Linkedin, Instagram, ExternalLink,
} from "lucide-react";

// ─────────────────────────────────────────────────────────────────────────────
// SellerBuyers — UNIFIED Buyers page (Option A from the design preview).
// One list to rule them all: invited, awaiting your approval, connected,
// at risk, prepay-only — with inline-editable terms, inline approve/decline,
// bulk invite, smart pre-fill, and the existing 360° drawer for any row.
// Legacy `#credit` deeplink still works and pre-applies the relevant filter
// so anyone bookmarking the old tab lands on something sensible.
// ─────────────────────────────────────────────────────────────────────────────

const TERMS_META = {
  net14:   { label: "Net-14",      sub: "Pay 14 days after invoice" },
  net30:   { label: "Net-30",      sub: "Pay 30 days after invoice" },
  net60:   { label: "Net-60",      sub: "Pay 60 days after invoice" },
  net90:   { label: "Net-90",      sub: "Pay 90 days after invoice" },
  prepaid: { label: "Prepay only", sub: "No credit extended" },
};

// v3 score bands: Excellent 780+ · Very Good 740 · Good 700 · Fair 640 · Building 580 · High risk <580.
const scoreColor = (s) =>
  s >= 780 ? "text-emerald-600"
    : s >= 740 ? "text-emerald-500"
      : s >= 700 ? "text-foreground"
        : s >= 640 ? "text-amber-600"
          : s >= 580 ? "text-orange-600"
            : "text-destructive";

const scoreBg = (s) =>
  s >= 780 ? "bg-emerald-50 border-emerald-200"
    : s >= 740 ? "bg-emerald-50/60 border-emerald-200/70"
      : s >= 700 ? "bg-secondary border-border"
        : s >= 640 ? "bg-amber-50 border-amber-200"
          : s >= 580 ? "bg-orange-50 border-orange-200"
            : "bg-rose-50 border-rose-200";

export default function SellerBuyers() {
  const location = useLocation();
  const nav = useNavigate();
  const [search, setSearch] = useSearchParams();

  // ── Sources of truth ──────────────────────────────────────────────────
  const [buyers, setBuyers] = useState([]);              // all buyers w/ score (connected via tenant)
  const [offers, setOffers] = useState([]);              // active credit offers
  const [invites, setInvites] = useState([]);            // invites table (pending/accepted/revoked)
  const [connections, setConnections] = useState([]);    // tenant connections (active + pending_seller_review + declined)
  const [tenant, setTenant] = useState(null);

  // ── UI state ──────────────────────────────────────────────────────────
  const [grantOpen, setGrantOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ buyer_email: "", terms: "net30", limit: 50000, note: "" });
  const [searchText, setSearchText] = useState("");
  // Composable facet filters managed by `SearchAndFilter`. Shape:
  //   { score: { op: 'lt', value: 600 },
  //     openAR: { op: 'gt', value: 5000 },
  //     terms:  { values: ['net30', 'net60'] },
  //     status: { values: ['connected'] },
  //     insured:{ on: true } }
  // Each key matches the facet `id` declared in the JSX below.
  const [filters, setFilters] = useState({});
  const [detailBuyerId, setDetailBuyerId] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);

  // Filter — `f` query param is the source of truth so the URL is shareable.
  // Legacy `#credit` hash deeplink redirects to ?filter=connected.
  const filter = search.get("filter") || "all";
  const setFilter = (k) => {
    const next = new URLSearchParams(search);
    if (k === "all") next.delete("filter"); else next.set("filter", k);
    setSearch(next, { replace: true });
  };
  useEffect(() => {
    if ((location.hash || "") === "#credit" && !search.get("filter")) {
      setFilter("connected");
      // Clear the legacy hash so it doesn't re-fire.
      nav(`${location.pathname}?filter=connected`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.hash]);

  // Tracks whether the initial /buyers + /tenant/connections fetch has
  // completed at least once. Without this, the SamplePreview component
  // flashes 3 fake demo buyers for the ~10-20s the parallel fetches take,
  // then swaps them out once real data arrives — a confusing first
  // impression for sellers who DO have connected buyers seeded. Gate the
  // SamplePreview on `initialLoaded` so we show a skeleton during fetch,
  // and the fake samples only render when we've CONFIRMED rows is empty.
  const [initialLoaded, setInitialLoaded] = useState(false);
  // Track load failures so we can surface an inline retry banner instead of
  // a silent fetch failure. Previously `load()` used `.catch(() => {})` which
  // hid every transient network/server hiccup and left the page in a
  // permanent skeleton state.
  const [loadError, setLoadError] = useState(null);
  const load = () => {
    setLoadError(null);
    return Promise.all([
      api.get("/buyers"),
      api.get("/seller/credit-offers"),
      api.get("/tenant/invites"),
      api.get("/tenant/connections"),
      api.get("/tenant/me"),
    ]).then(([b, o, i, c, t]) => {
      setBuyers(b.data); setOffers(o.data);
      setInvites(i.data || []); setConnections(c.data || []);
      setTenant(t.data);
    }).catch((err) => {
      const msg = err?.response?.data?.detail || err?.message || "Could not load buyers";
      setLoadError(typeof msg === "string" ? msg : "Could not load buyers");
      toast.error(typeof msg === "string" ? msg : "Could not load buyers");
    }).finally(() => setInitialLoaded(true));
  };
  useEffect(() => { load(); }, []);

  const buyerById = useMemo(() => {
    const m = {};
    buyers.forEach(b => { m[b.id] = b; });
    return m;
  }, [buyers]);
  const buyerByEmail = useMemo(() => {
    const m = {};
    buyers.forEach(b => { m[(b.email || "").toLowerCase()] = b; });
    return m;
  }, [buyers]);

  const activeOffers = offers.filter(o => o.status === "active");
  const offerByEmail = useMemo(() => {
    const m = {};
    activeOffers.forEach(o => { m[(o.buyer_email || "").toLowerCase()] = o; });
    return m;
  }, [activeOffers]);

  // ── Build the unified row list ────────────────────────────────────────
  // Each row represents ONE buyer in ONE state. We never show the same
  // person twice. Priority of state resolution:
  //   1. Connection in pending_seller_review → status: "awaiting"
  //   2. Connection in declined              → status: "declined"
  //   3. Connection in active                → status: "connected"
  //   4. Invite in pending                   → status: "invited"
  //   5. Invite in revoked / accepted but no connection (rare) → "revoked"
  const rows = useMemo(() => {
    const out = [];
    // Index connections by buyer_id and by buyer email (via buyerById).
    const connByBuyerId = {};
    connections.forEach(c => { connByBuyerId[c.buyer_id] = c; });

    // First, every buyer that has a connection on this tenant.
    buyers.forEach(b => {
      const conn = connByBuyerId[b.id];
      if (!conn) return;  // not connected on THIS tenant
      const offer = offerByEmail[(b.email || "").toLowerCase()] || null;
      let status = "connected";
      if (conn.status === "pending_seller_review") status = "awaiting";
      else if (conn.status === "declined") status = "declined";
      else if (conn.status === "active") status = "connected";
      else status = conn.status || "connected";
      out.push({
        kind: "buyer",
        id: `buyer-${b.id}`,
        buyer: b,
        connection: conn,
        offer,
        status,
        score: b.score,
        rating: b.rating,
        risk: detectRisk(b),
      });
    });
    // Then, invites without a connection (pending / revoked).
    invites.forEach(inv => {
      // If the email matches a connected buyer, we already showed that row.
      const matched = buyerByEmail[(inv.email || "").toLowerCase()];
      if (matched && connByBuyerId[matched.id]) return;
      const offer = offerByEmail[(inv.email || "").toLowerCase()] || null;
      let status = "invited";
      if (inv.status === "revoked") status = "revoked";
      out.push({
        kind: "invite",
        id: `invite-${inv.id}`,
        invite: inv,
        offer,
        status,
        score: matched?.score || null,
        rating: matched?.rating || null,
        risk: matched ? detectRisk(matched) : null,
        buyer: matched || null,
      });
    });
    return out;
  }, [buyers, connections, invites, offerByEmail, buyerByEmail]);

  // ── Filter chips + facet bar ─────────────────────────────────────────
  const filtered = useMemo(() => {
    let xs = rows;
    if (filter === "connected") xs = xs.filter(r => r.status === "connected");
    else if (filter === "invited") xs = xs.filter(r => r.status === "invited");
    else if (filter === "awaiting") xs = xs.filter(r => r.status === "awaiting");
    else if (filter === "atrisk") xs = xs.filter(r => !!r.risk);
    else if (filter === "prepay") xs = xs.filter(r => r.offer?.terms === "prepaid");
    if (searchText.trim()) {
      const q = searchText.trim().toLowerCase();
      xs = xs.filter(r => {
        const email = r.buyer?.email || r.invite?.email || "";
        const name = r.buyer?.name || r.buyer?.company || "";
        return email.toLowerCase().includes(q) || name.toLowerCase().includes(q);
      });
    }
    // ── Facet overlay (composes on top of chips + search) ──────────────
    const F = filters || {};
    if (F.score?.value != null || (F.score?.op === "between" && F.score.min != null)) {
      xs = xs.filter(r => {
        const s = r.score;
        if (!s) return false;       // no score → can't satisfy a numeric filter
        if (F.score.op === "gt") return s > Number(F.score.value);
        if (F.score.op === "lt") return s < Number(F.score.value);
        if (F.score.op === "between") return s >= Number(F.score.min) && s <= Number(F.score.max);
        return true;
      });
    }
    if (F.openAR?.value != null || (F.openAR?.op === "between" && F.openAR.min != null)) {
      xs = xs.filter(r => {
        const v = Number(r.offer?.used || 0);
        if (F.openAR.op === "gt") return v > Number(F.openAR.value);
        if (F.openAR.op === "lt") return v < Number(F.openAR.value);
        if (F.openAR.op === "between") return v >= Number(F.openAR.min) && v <= Number(F.openAR.max);
        return true;
      });
    }
    if (F.terms?.values?.length) {
      xs = xs.filter(r => F.terms.values.includes(r.offer?.terms));
    }
    if (F.statusFacet?.values?.length) {
      xs = xs.filter(r => F.statusFacet.values.includes(r.status));
    }
    if (F.insured?.on != null) {
      xs = xs.filter(r => F.insured.on ? !!r.connection?.insured : !r.connection?.insured);
    }
    if (F.risk?.values?.length) {
      xs = xs.filter(r => F.risk.values.includes(r.risk?.kind));
    }
    return xs;
  }, [rows, filter, searchText, filters]);

  const counts = useMemo(() => ({
    all: rows.length,
    connected: rows.filter(r => r.status === "connected").length,
    invited:   rows.filter(r => r.status === "invited").length,
    awaiting:  rows.filter(r => r.status === "awaiting").length,
    atrisk:    rows.filter(r => r.risk).length,
    prepay:    rows.filter(r => r.offer?.terms === "prepaid").length,
  }), [rows]);

  // ── Aggregate KPIs (top action center) ───────────────────────────────
  const totalAR = useMemo(() => activeOffers.reduce((s, o) => s + (o.used || 0), 0), [activeOffers]);

  // ── Open Grant / Edit dialog (existing flows) ───────────────────────
  const openGrant = (forBuyerEmail) => {
    setEditing(null);
    setForm({ buyer_email: forBuyerEmail || "", terms: "net30", limit: 50000, note: "" });
    setGrantOpen(true);
  };
  const openEdit = (offer) => {
    setEditing(offer);
    setForm({
      buyer_email: offer.buyer_email,
      terms: offer.terms,
      limit: offer.limit,
      note: offer.note || "",
    });
    setGrantOpen(true);
  };
  const submit = async () => {
    if (!form.buyer_email) return toast.error("Enter buyer email");
    if (!form.limit || Number(form.limit) <= 0) return toast.error("Enter a credit limit");
    try {
      if (editing) {
        await api.patch(`/seller/credit-offers/${editing.id}`, {
          terms: form.terms, limit: Number(form.limit), note: form.note,
        });
        toast.success("Offer updated");
      } else {
        await api.post("/seller/credit-offers", {
          buyer_email: form.buyer_email.trim().toLowerCase(),
          terms: form.terms, limit: Number(form.limit), note: form.note,
        });
        toast.success("Free terms granted — buyer notified");
      }
      setGrantOpen(false); setEditing(null); load();
    } catch (e) {
      errorToastFromAxios(e, "Couldn't save offer", {
        subject: `${editing ? "Couldn't update" : "Couldn't grant"} free terms for ${form.buyer_email}`,
        body:
          `I tried to ${editing ? "update" : "grant"} a free credit offer to ${form.buyer_email}.\n` +
          `Terms: ${form.terms} · Limit: ${money(Number(form.limit) || 0)}\n` +
          `Note: ${form.note || "(none)"}`,
        urgency: "normal",
        category: "credit-terms",
      });
    }
  };
  const revoke = async (offer) => {
    if (!window.confirm(`Revoke ${TERMS_META[offer.terms]?.label || offer.terms} policy for ${offer.buyer_email}?`)) return;
    try {
      await api.delete(`/seller/credit-offers/${offer.id}`);
      toast.success("Policy revoked — buyer notified");
      load();
    } catch { toast.error("Could not revoke"); }
  };

  // ── Inline action handlers ───────────────────────────────────────────
  const approveConn = async (cid) => {
    try { await api.post(`/seller/connections/${cid}/approve`); toast.success("Buyer approved"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not approve"); }
  };
  const declineConn = async (cid) => {
    if (!window.confirm("Decline this buyer's connection? They'll be notified.")) return;
    try { await api.post(`/seller/connections/${cid}/decline`); toast.success("Declined"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not decline"); }
  };
  const resendInvite = async (id) => {
    try { await api.post(`/tenant/invites/${id}/resend`); toast.success("Invite re-sent"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not resend"); }
  };
  const revokeInvite = async (id) => {
    if (!window.confirm("Revoke this invite?")) return;
    try { await api.delete(`/tenant/invites/${id}`); toast.success("Revoked"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not revoke"); }
  };
  const updateTerms = async (row, newTerms) => {
    try {
      if (row.offer) {
        await api.patch(`/seller/credit-offers/${row.offer.id}`, {
          terms: newTerms, limit: row.offer.limit, note: row.offer.note,
        });
      } else {
        const email = row.buyer?.email || row.invite?.email;
        if (!email) return;
        await api.post("/seller/credit-offers", {
          buyer_email: email.toLowerCase(),
          terms: newTerms,
          limit: newTerms === "prepaid" ? 0 : 50000,
          note: null,
        });
      }
      toast.success("Terms updated"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update"); }
  };

  // ── Render ───────────────────────────────────────────────────────────
  return (
    <div data-testid="seller-buyers">
      <PageHeader
        title="Buyers"
        subtitle="Connect, vet, and set terms — all in one place."
        action={
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={async () => {
                try {
                  const r = await api.get("/seller/buyers/export", { responseType: "blob" });
                  const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
                  const a = document.createElement("a");
                  a.href = url; a.download = `buyers-${new Date().toISOString().slice(0,10)}.csv`; a.click();
                  URL.revokeObjectURL(url);
                  toast.success("Buyers CSV downloaded");
                } catch (e) { toast.error("Export failed"); }
              }}
              data-testid="export-buyers-csv"
              className="rounded-full h-10 px-4"
            >
              <Download className="w-4 h-4 mr-1.5"/>Export CSV
            </Button>
            <Button
              variant="outline"
              onClick={() => setBulkOpen(true)}
              data-testid="bulk-invite-btn"
              className="rounded-full h-10 px-4"
            >
              <Mail className="w-4 h-4 mr-1.5"/>Bulk invite
            </Button>
            <Button
              onClick={() => setInviteOpen(true)}
              data-testid="invite-buyer-btn"
              className="rounded-full bg-foreground text-background hover:bg-foreground/90 h-10 px-4"
            >
              <Plus className="w-4 h-4 mr-1.5"/>Invite a buyer
            </Button>
          </div>
        }
      />

      <div className="p-8 space-y-6">

        {/* ── Action Center (only renders cards with count > 0) ── */}
        <ActionCenter
          counts={counts}
          totalAR={totalAR}
          onJump={setFilter}
        />

        {/* ── KPI strip ── */}
        <section className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="buyers-kpis">
          <KPI label="Connected"  value={counts.connected}  hint="Active accounts"/>
          <KPI label="Invited"    value={counts.invited}    hint="Pending acceptance"/>
          <KPI label="Awaiting you" value={counts.awaiting} hint="Manual approval"/>
          <KPI label="Open AR"    value={money(totalAR)}    hint="Across all buyers"/>
        </section>

        {/* ── Filter chips ── */}
        <div className="flex items-center gap-2 flex-wrap" data-testid="buyer-filters">
          {[
            { k: "all",       label: "All" },
            { k: "connected", label: "Connected" },
            { k: "invited",   label: "Invited" },
            { k: "awaiting",  label: "Awaiting you" },
            { k: "atrisk",    label: "At risk" },
            { k: "prepay",    label: "Prepay only" },
          ].map(f => {
            const active = filter === f.k;
            const c = counts[f.k];
            return (
              <button
                key={f.k}
                onClick={() => setFilter(f.k)}
                data-testid={`filter-chip-${f.k}`}
                className={`inline-flex items-center gap-1.5 px-3 h-9 rounded-full text-sm border transition-colors ${active ? "bg-foreground text-background border-foreground" : "border-border hover:bg-secondary"}`}
              >
                {f.label}
                <span className={`text-[10.5px] px-1.5 rounded-full font-mono ${active ? "bg-background/20" : "bg-secondary"}`}>{c}</span>
              </button>
            );
          })}
        </div>

        {/* ── Power-user facet bar (composes on top of the chips above) ── */}
        <SearchAndFilter
          page="seller_buyers"
          search={searchText}
          onSearchChange={setSearchText}
          filters={filters}
          onChange={setFilters}
          totalCount={filtered.length}
          facets={[
            { id: "score",  label: "Score",     icon: TrendingUp, type: "numeric" },
            { id: "openAR", label: "Open AR",   icon: DollarSign, type: "numeric", numericLabel: "$" },
            {
              id: "terms", label: "Terms", icon: Receipt, type: "multi",
              options: Object.entries(TERMS_META).map(([v, m]) => ({ value: v, label: m.label })),
            },
            {
              id: "statusFacet", label: "Status", icon: Tag, type: "multi",
              options: [
                { value: "connected", label: "Connected" },
                { value: "invited",   label: "Invited" },
                { value: "awaiting",  label: "Awaiting you" },
                { value: "declined",  label: "Declined" },
                { value: "revoked",   label: "Revoked" },
              ],
            },
            {
              id: "risk", label: "Risk flag", icon: AlertTriangle, type: "multi",
              options: [
                { value: "score_low",      label: "Score low (< 600)" },
                { value: "score_marginal", label: "Marginal (600-650)" },
              ],
            },
            { id: "insured", label: "Insured", icon: Shield, type: "boolean" },
          ]}
        />

        {/* ── Storefront link helper ── */}
        {tenant && (
          <div className="rounded-xl border border-foreground/10 bg-secondary/30 p-3 flex flex-wrap items-center gap-3 text-[12.5px]">
            <span className="text-foreground/55">Your shareable buyer link:</span>
            <code className="mono px-2 py-0.5 rounded bg-white border border-foreground/10">{window.location.origin}/buyer/catalog?supplier={tenant.slug}</code>
            <button
              onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/buyer/catalog?supplier=${tenant.slug}`); toast.success("Copied"); }}
              className="text-foreground/60 hover:text-foreground"
              data-testid="copy-storefront-url"
            >Copy link</button>
          </div>
        )}

        {/* ── Unified table ── */}
        {loadError && (
          <div
            className="rounded-xl border border-rose-200 bg-rose-50 p-3 flex items-start gap-3 text-[12.5px]"
            data-testid="buyers-load-error"
          >
            <AlertCircle className="w-4 h-4 text-rose-700 mt-0.5 shrink-0"/>
            <div className="flex-1 text-rose-900">
              <strong className="block">Could not load buyers</strong>
              <span className="text-rose-800/80">{loadError}</span>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => load()}
              data-testid="buyers-load-retry"
              className="rounded-full h-8 px-3 border-rose-200 text-rose-900 hover:bg-rose-100"
            >
              <RefreshCw className="w-3.5 h-3.5 mr-1.5"/>Retry
            </Button>
          </div>
        )}
        <div className="rounded-2xl border border-border bg-white overflow-hidden" data-testid="buyers-table">
          <div className="grid grid-cols-[2fr_120px_70px_120px_110px_140px_110px_140px] gap-4 px-5 py-3 bg-secondary/50 border-b border-border text-[10px] mono uppercase tracking-[0.2em] text-foreground/55">
            <div>Buyer</div><div>Status</div><div className="text-center">Score</div>
            <div>Risk</div><div>Open AR</div><div>Terms</div><div>Last order</div><div className="text-right">Actions</div>
          </div>
          <div className="divide-y divide-border">
            {filtered.length === 0 && (
              <>
                {/* Sample preview — only renders AFTER the initial fetch has
                 *  resolved AND we've confirmed rows is empty. During the
                 *  fetch window we show a lightweight skeleton so sellers
                 *  with real connections never see the fake samples flash. */}
                {!initialLoaded ? (
                  <div className="px-5 py-10 space-y-3" data-testid="buyers-loading">
                    {[0, 1, 2].map(i => (
                      <div key={i} className="grid grid-cols-[2fr_120px_70px_120px_110px_140px_110px_140px] gap-4 items-center animate-pulse">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-secondary"/>
                          <div className="space-y-1.5">
                            <div className="h-3 w-32 bg-secondary rounded"/>
                            <div className="h-2.5 w-20 bg-secondary/60 rounded"/>
                          </div>
                        </div>
                        <div className="h-3 w-16 bg-secondary rounded"/>
                        <div className="h-3 w-8 bg-secondary rounded mx-auto"/>
                        <div className="h-3 w-14 bg-secondary rounded"/>
                        <div className="h-3 w-16 bg-secondary rounded"/>
                        <div className="h-3 w-12 bg-secondary rounded"/>
                        <div className="h-3 w-16 bg-secondary rounded"/>
                        <div className="h-3 w-14 bg-secondary rounded justify-self-end"/>
                      </div>
                    ))}
                  </div>
                ) : searchText.trim() === ""
                  && filter === "all"
                  && rows.length === 0
                  && Object.keys(filters || {}).length === 0
                  ? <SampleBuyersPreview onInvite={() => setInviteOpen(true)} />
                  : (
                    <div className="px-5 py-14 text-center text-sm text-foreground/50" data-testid="buyers-empty">
                      {searchText
                        ? `No buyers match "${searchText}"`
                        : filter === "awaiting"
                        ? "Nothing awaiting your approval — turn on manual approval in Settings to vet new buyers."
                        : "Nothing here yet."}
                    </div>
                  )}
              </>
            )}
            {filtered.map(row => (
              <UnifiedRow
                key={row.id}
                row={row}
                onOpen={() => row.buyer && setDetailBuyerId(row.buyer.id)}
                onApprove={(cid) => approveConn(cid)}
                onDecline={(cid) => declineConn(cid)}
                onResend={(id) => resendInvite(id)}
                onRevokeInvite={(id) => revokeInvite(id)}
                onChangeTerms={(t) => updateTerms(row, t)}
                onGrant={(email) => openGrant(email)}
                onEdit={(offer) => openEdit(offer)}
                onRevokeOffer={(offer) => revoke(offer)}
                onInsure={(bid) => nav(`/seller/insurance?buyer=${bid}&action=request`)}
                onMessage={(uid) => nav(`/seller/messages?counterpart=${uid}`)}
              />
            ))}
          </div>
        </div>

        {/* ── Smart pre-fill helper banner ── */}
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 flex items-start gap-3 text-[12.5px]" data-testid="smart-prefill-banner">
          <Sparkles className="w-4 h-4 text-emerald-700 mt-0.5"/>
          <div className="text-emerald-900">
            <strong className="block">Smart pre-fill on Invite</strong>
            When you invite an email that already exists on verqo, we look up the buyer's score and pre-fill terms — Net-60 if score ≥ 720, Net-30 if 650-720, Prepay if &lt; 650. One click to send.
          </div>
        </div>
      </div>

      {/* ── Existing 360° drawer ── */}
      <BuyerDetailDrawer
        buyerId={detailBuyerId}
        onClose={() => setDetailBuyerId(null)}
        onGrant={(email) => { setDetailBuyerId(null); openGrant(email); }}
        onEdit={(offer) => { setDetailBuyerId(null); openEdit(offer); }}
        onRevoke={(offer) => { setDetailBuyerId(null); revoke(offer); }}
        onInsure={(bid) => { setDetailBuyerId(null); nav(`/seller/insurance?buyer=${bid}&action=request`); }}
      />

      {/* ── Existing Grant dialog (kept verbatim) ── */}
      <GrantDialog
        open={grantOpen}
        onOpenChange={(v) => { if (!v) { setGrantOpen(false); setEditing(null); } }}
        editing={editing}
        form={form}
        setForm={setForm}
        buyerByEmail={buyerByEmail}
        onSubmit={submit}
        onCancel={() => { setGrantOpen(false); setEditing(null); }}
      />

      {/* ── New: Single-buyer invite dialog with smart pre-fill ── */}
      <SingleInviteDialog
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onDone={() => { setInviteOpen(false); load(); }}
      />

      {/* ── New: Bulk invite (paste emails) ── */}
      <BulkInviteDialog
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        onDone={() => { setBulkOpen(false); load(); }}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────

// Risk detector — surfaces a chip on the row when a buyer's score is low
// or trending down. Wired loosely off the existing score signals so it
// doesn't need a new endpoint.
function detectRisk(b) {
  if (!b || !b.score) return null;
  if (b.score < 600) return { kind: "score_low", label: "Score low" };
  if (b.score < 650) return { kind: "score_marginal", label: "Marginal" };
  // Future: parse signals.payment_history + signals.utilization for drop trends.
  return null;
}

function ActionCenter({ counts, totalAR, onJump }) {
  // Compose only the cards with non-zero counts so the strip "appears"
  // when there's something to do, and disappears entirely when it's a
  // quiet day. This is the single biggest "less-clicks" win on the page.
  const cards = [];
  if (counts.awaiting > 0) cards.push({ kind: "awaiting", value: counts.awaiting, label: "Awaiting your approval", hint: "Self-signed up — review", icon: Inbox, accent: "indigo", filter: "awaiting" });
  if (counts.atrisk > 0)   cards.push({ kind: "atrisk",   value: counts.atrisk,   label: "At-risk buyers",          hint: "Score / payment alerts",   icon: AlertTriangle, accent: "rose", filter: "atrisk" });
  if (counts.invited > 0)  cards.push({ kind: "invited",  value: counts.invited,  label: "Pending invites",         hint: "Tap to follow up",         icon: Mail, accent: "amber", filter: "invited" });

  if (cards.length === 0) return null;
  const tone = {
    indigo: "border-indigo-200 bg-indigo-50 text-indigo-900",
    rose:   "border-rose-200 bg-rose-50 text-rose-900",
    amber:  "border-amber-200 bg-amber-50 text-amber-900",
  };
  return (
    <section className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="action-center">
      {cards.map(c => (
        <button
          key={c.kind}
          onClick={() => onJump(c.filter)}
          data-testid={`action-card-${c.kind}`}
          className={`text-left rounded-xl border p-3.5 transition-all hover:scale-[1.01] hover:shadow-sm ${tone[c.accent]}`}
        >
          <div className="flex items-center justify-between">
            <c.icon className="w-4 h-4 opacity-80"/>
            <ChevronRight className="w-4 h-4 opacity-60"/>
          </div>
          <div className="display text-2xl mt-2">{c.value}</div>
          <div className="text-[12px] font-semibold mt-0.5">{c.label}</div>
          <div className="text-[11px] opacity-70 mt-0.5">{c.hint} →</div>
        </button>
      ))}
    </section>
  );
}

// Small score badge used in the unified table.
function ScoreBadge({ score }) {
  const sc = score || 0;
  return (
    <div className={`inline-flex items-center justify-center w-12 h-7 rounded-md border text-[12.5px] font-semibold ${scoreBg(sc)} ${scoreColor(sc)}`}>
      {score ? score : "—"}
    </div>
  );
}

// Allianz Trade grade chip — appears next to the buyer name when the
// Allianz Company-Search/Grade lookup landed during the post-KYB hook.
// Greyed out when the company wasn't found in Allianz's database.
//
// AllianzCoverPanel — richer in-drawer surface of the cover decision.
// Visually splits the buyer-facing credit grant (NOT shown here) from the
// lender-facing cover (the only number that matters for factoring).
//
// Phase SC-5.2 · "Request limit bump" CTA. Sellers who hit a cover ceiling
// (e.g., $100k cover but a $200k invoice they want to factor) can request
// Allianz to increase the limit for THIS buyer directly from the panel.
// Backed by existing POST /api/insurance/v2/buyer-covers/request.
function AllianzCoverPanel({ cover, buyerId, buyerName, onUpdate }) {
  const [bumpOpen, setBumpOpen] = useState(false);
  if (!cover) return null;
  const fmt = (n) => Number(n || 0).toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
  const status = cover.status;
  const tone = status === "accepted" ? "emerald"
    : status === "pending" || status === "investigating" ? "amber"
    : status === "not_underwritten" ? "slate"
    : "rose";
  const toneCls = {
    emerald: "border-emerald-200 bg-emerald-50",
    amber:   "border-amber-200 bg-amber-50",
    slate:   "border-foreground/15 bg-secondary/40",
    rose:    "border-rose-200 bg-rose-50",
  }[tone];
  const titleCls = {
    emerald: "text-emerald-700",
    amber:   "text-amber-700",
    slate:   "text-foreground/60",
    rose:    "text-rose-700",
  }[tone];
  const headline = {
    accepted: `Allianz cover · ${fmt(cover.limit_usd)}`,
    pending:  "Allianz underwriting in progress",
    investigating: "Allianz underwriting in progress",
    declined: "Allianz declined cover",
    refused:  "Allianz refused cover",
    revoked:  "Allianz cover revoked",
    not_underwritten: "Not yet underwritten by Allianz",
  }[status] || `Cover · ${status}`;

  // Bump is allowed when there's an active cover OR no cover yet but the
  // buyer has been declined / not yet underwritten — in both cases the seller
  // can ask Allianz to re-evaluate at a higher limit.
  const canBump = status !== "pending" && status !== "investigating";

  return (
    <div className={`rounded-xl border p-4 ${toneCls}`} data-testid="allianz-cover-panel">
      <div className="flex items-center justify-between gap-3 mb-2">
        <div className={`text-[11px] mono uppercase tracking-[0.18em] ${titleCls}`}>
          Allianz Trade · Receivables Cover
        </div>
        <span className="chip bg-foreground/5 text-foreground/55 border border-foreground/15 text-[10px]">
          Lender-facing · not buyer's credit line
        </span>
      </div>
      <div className="flex items-center justify-between gap-3">
        <div className="text-sm font-medium text-foreground">{headline}</div>
        {canBump && buyerId && (
          <Button
            size="sm" variant="outline"
            onClick={() => setBumpOpen(true)}
            data-testid="cover-request-bump"
            className="rounded-full h-7 text-[11.5px]"
          >
            <TrendingUp className="w-3 h-3 mr-1"/>
            {status === "accepted" ? "Request limit bump" : "Re-apply for cover"}
          </Button>
        )}
      </div>
      {status === "accepted" && (
        <div className="mt-2 grid grid-cols-3 gap-3 text-[12px]">
          <div>
            <div className="text-[10px] mono uppercase tracking-wider text-foreground/55">Approved limit</div>
            <div className="display text-base mt-0.5 tabular-nums">{fmt(cover.limit_usd)}</div>
          </div>
          <div>
            <div className="text-[10px] mono uppercase tracking-wider text-foreground/55">Coverage</div>
            <div className="text-sm mt-0.5">{(cover.coverage_pct * 100).toFixed(0)}%<span className="text-foreground/55"> non-recourse</span></div>
          </div>
          <div>
            <div className="text-[10px] mono uppercase tracking-wider text-foreground/55">Scope</div>
            <div className="text-sm mt-0.5 capitalize">{cover.scoping === "seller_specific" ? "You ↔ buyer" : "Baseline (all sellers)"}</div>
          </div>
        </div>
      )}
      {cover.refusal_reason && (
        <div className="mt-2 text-[12px] text-foreground/65 italic">{cover.refusal_reason}</div>
      )}
      <div className="mt-2 text-[11px] text-foreground/50 leading-relaxed">
        This is what Allianz Trade will reimburse the lender for if the buyer defaults — independent of the buyer's own credit line.
        {cover.is_mock && <span className="text-foreground/40"> · simulated until live Allianz API is wired</span>}
      </div>

      <CoverBumpDialog
        open={bumpOpen}
        onClose={() => setBumpOpen(false)}
        buyerId={buyerId}
        buyerName={buyerName}
        currentLimit={cover.limit_usd || 0}
        onUpdated={(fresh) => { setBumpOpen(false); onUpdate?.(fresh); }}
      />
    </div>
  );
}

// CoverBumpDialog — minimal "ask Allianz for more limit on this buyer"
// modal. Pre-fills with 2× current limit (or $250k if no current cover).
function CoverBumpDialog({ open, onClose, buyerId, buyerName, currentLimit, onUpdated }) {
  const defaultLimit = Math.max(Math.round((currentLimit || 0) * 2), 50_000);
  const [limit, setLimit] = useState(defaultLimit);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) setLimit(Math.max(Math.round((currentLimit || 0) * 2), 50_000));
  }, [open, currentLimit]);

  const submit = async () => {
    if (Number(limit) <= 0) {
      toast.error("Enter a limit greater than zero");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/insurance/v2/buyer-covers/request", {
        buyer_id: buyerId, requested_limit: Number(limit),
      });
      const newStatus = data?.status;
      if (newStatus === "accepted") {
        toast.success(`Allianz approved ${money(data?.permanent_limit || 0)} on ${buyerName || "this buyer"}`);
      } else if (newStatus === "investigating" || newStatus === "pending") {
        toast.info("Allianz is investigating — decision typically within 24h");
      } else {
        toast.warning(`Allianz response: ${newStatus} — ${data?.refusal_reason || "see drawer for details"}`);
      }
      onUpdated?.(data);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Cover request failed";
      toast.error(typeof msg === "string" ? msg : "Cover request failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !busy && !v && onClose?.()}>
      <DialogContent data-testid="cover-bump-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="display tracking-tight">Request Allianz limit bump</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-1">
          <div className="text-[12.5px] text-foreground/70 leading-relaxed">
            Ask Allianz Trade to extend more receivables cover on <span className="font-medium">{buyerName || "this buyer"}</span>.
            Approval gives the lender more headroom to factor / fund larger invoices — and is independent of {buyerName ? `${buyerName}'s` : "the buyer's"} own credit line.
          </div>
          <div className="rounded-lg bg-secondary/40 border border-border p-3 text-[12px] grid grid-cols-2 gap-3">
            <div>
              <div className="text-[10px] mono uppercase tracking-wider text-foreground/55">Current cover</div>
              <div className="text-sm mt-0.5 tabular-nums">{money(currentLimit || 0)}</div>
            </div>
            <div>
              <div className="text-[10px] mono uppercase tracking-wider text-foreground/55">Requested</div>
              <div className="text-sm mt-0.5 tabular-nums text-emerald-700">{money(Number(limit) || 0)}</div>
            </div>
          </div>
          <div>
            <Label htmlFor="bump-limit" className="text-[12px]">New requested limit (USD)</Label>
            <Input
              id="bump-limit" type="number" min={1} step={1000}
              value={limit}
              onChange={(e) => setLimit(e.target.value)}
              disabled={busy}
              data-testid="cover-bump-amount"
              className="mt-1"
            />
            <div className="text-[11px] text-foreground/50 mt-1.5">
              Typical bumps: 2× current limit. Anything above $1M usually triggers manual underwriting.
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button
            onClick={submit} disabled={busy || Number(limit) <= 0}
            data-testid="cover-bump-submit"
            className="rounded-full bg-foreground text-background hover:bg-foreground/90">
            {busy ? "Sending to Allianz…" : "Request bump"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Phase SC-5 · CoverChip surfaces the buyer-specific Allianz receivables-
// cover decision (the lender-facing limit, distinct from the buyer's own
// credit grant). Shows the seller exactly how much factoring/take-terms
// the lender is insured for on this buyer.
function CoverChip({ buyer }) {
  const c = buyer?.allianz_cover;
  if (!c) return null;
  const fmt = (n) => `$${Math.round(Number(n || 0) / 1000)}k`;
  if (c.status === "not_underwritten") {
    return (
      <span
        className="inline-flex items-center gap-1 text-[9.5px] mono uppercase tracking-[0.14em] px-1.5 py-0.5 rounded-full bg-foreground/5 text-foreground/40 border border-foreground/15"
        title="Buyer hasn't been underwritten by Allianz yet"
        data-testid={`cover-chip-${buyer?.id || "x"}`}
      >
        Cover · n/a
      </span>
    );
  }
  if (c.status === "declined" || c.status === "refused" || c.status === "revoked") {
    return (
      <span
        className="inline-flex items-center gap-1 text-[9.5px] mono uppercase tracking-[0.14em] px-1.5 py-0.5 rounded-full bg-rose-50 text-rose-700 border border-rose-200"
        title={c.refusal_reason || `Cover ${c.status}`}
        data-testid={`cover-chip-declined-${buyer?.id || "x"}`}
      >
        <ShieldCheck className="w-2.5 h-2.5"/>No cover
      </span>
    );
  }
  if (c.status === "pending" || c.status === "investigating") {
    return (
      <span
        className="inline-flex items-center gap-1 text-[9.5px] mono uppercase tracking-[0.14em] px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200"
        title="Allianz underwriting in progress"
        data-testid={`cover-chip-pending-${buyer?.id || "x"}`}
      >
        <ShieldCheck className="w-2.5 h-2.5"/>Cover · pending
      </span>
    );
  }
  // accepted
  return (
    <span
      className="inline-flex items-center gap-1 text-[9.5px] mono uppercase tracking-[0.14em] px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200"
      title={`Allianz cover · ${fmt(c.limit_usd)} · ${(c.coverage_pct * 100).toFixed(0)}% non-recourse${c.is_mock ? " · simulated until live Allianz wired" : ""}`}
      data-testid={`cover-chip-accepted-${buyer?.id || "x"}`}
    >
      <ShieldCheck className="w-2.5 h-2.5"/>Cover {fmt(c.limit_usd)}
    </span>
  );
}

function AllianzGradeChip({ buyer, compact = false }) {
  const g = buyer?.allianz_grade;
  if (!g) return null;
  if (g.found === false) {
    if (compact) return null;
    return (
      <span
        className="inline-flex items-center gap-1 text-[9.5px] mono uppercase tracking-[0.14em] px-1.5 py-0.5 rounded-full bg-foreground/5 text-foreground/40 border border-foreground/15"
        title="Not in Allianz Trade database"
        data-testid="allianz-grade-na"
      >
        Allianz · n/a
      </span>
    );
  }
  if (!g.grade) return null;
  // Tier-based color (10 → emerald, 1 → rose)
  const tier = g.tier || 5;
  const cls = tier >= 8 ? "bg-emerald-50 text-emerald-800 border-emerald-200"
    : tier >= 6 ? "bg-blue-50 text-blue-800 border-blue-200"
    : tier >= 4 ? "bg-amber-50 text-amber-800 border-amber-200"
    : "bg-rose-50 text-rose-800 border-rose-200";
  return (
    <span
      className={`inline-flex items-center gap-1 text-[9.5px] mono uppercase tracking-[0.14em] px-1.5 py-0.5 rounded-full border ${cls}`}
      title={`Allianz Trade grade: ${g.grade}${g.outlook ? ` · ${g.outlook}` : ""}${g.is_mock ? " · simulated" : ""}`}
      data-testid={`allianz-grade-chip-${buyer?.id || "x"}`}
    >
      <Shield className="w-2.5 h-2.5"/>
      Allianz {g.grade}
    </span>
  );
}

function StatusPill({ status }) {
  const meta = {
    connected: { label: "Connected",   cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
    invited:   { label: "Invite sent", cls: "bg-amber-50 text-amber-700 border-amber-200" },
    awaiting:  { label: "Awaiting you", cls: "bg-indigo-50 text-indigo-700 border-indigo-200" },
    declined:  { label: "Declined",    cls: "bg-rose-50 text-rose-700 border-rose-200" },
    revoked:   { label: "Revoked",     cls: "bg-foreground/5 text-foreground/50 border-foreground/15" },
  }[status] || { label: status, cls: "bg-foreground/5 text-foreground/50 border-foreground/15" };
  return (
    <span className={`inline-flex items-center text-[10.5px] mono uppercase tracking-[0.16em] px-2 py-0.5 rounded-full border ${meta.cls}`}>
      {meta.label}
    </span>
  );
}

function RiskChip({ risk }) {
  if (!risk) return <span className="text-foreground/30">—</span>;
  return (
    <span className="inline-flex items-center gap-1 text-[10.5px] mono uppercase tracking-[0.16em] px-2 py-0.5 rounded-full bg-rose-50 text-rose-700 border border-rose-200">
      <AlertTriangle className="w-3 h-3"/>{risk.label}
    </span>
  );
}

function InlineTermsCell({ row, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    setTimeout(() => document.addEventListener("click", handler), 0);
    return () => document.removeEventListener("click", handler);
  }, [open]);
  // Don't render an editor for invites that haven't been accepted —
  // there's no offer to attach until the buyer signs up.
  if (row.status === "awaiting" || row.status === "declined" || row.status === "revoked") {
    return <span className="text-foreground/30">—</span>;
  }
  const cur = row.offer?.terms || null;
  const label = cur ? TERMS_META[cur]?.label : "— set —";
  return (
    <div className="relative" ref={ref} onClick={(e) => e.stopPropagation()}>
      <button
        onClick={() => setOpen(o => !o)}
        data-testid={`terms-cell-${row.id}`}
        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md border text-[12.5px] hover:bg-secondary ${cur ? "border-border bg-secondary" : "border-dashed border-foreground/30 text-foreground/50 italic"}`}
      >
        {label}
        <ChevronDown className="w-3 h-3 text-foreground/40"/>
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 z-20 rounded-xl border border-border bg-white shadow-lg p-1 min-w-[180px]" data-testid={`terms-popover-${row.id}`}>
          {Object.entries(TERMS_META).map(([k, v]) => (
            <button
              key={k}
              onClick={() => { onChange(k); setOpen(false); }}
              data-testid={`terms-opt-${k}-${row.id}`}
              className={`w-full text-left px-3 py-2 rounded-lg text-[13px] hover:bg-secondary flex items-center justify-between ${cur === k ? "bg-secondary" : ""}`}
            >
              <div>
                <div className="font-medium">{v.label}</div>
                <div className="text-[11px] text-foreground/55">{v.sub}</div>
              </div>
              {cur === k && <Check className="w-3.5 h-3.5"/>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function UnifiedRow({
  row, onOpen, onApprove, onDecline, onResend, onRevokeInvite,
  onChangeTerms, onGrant, onEdit, onRevokeOffer, onInsure, onMessage,
}) {
  const email = row.buyer?.email || row.invite?.email;
  const displayName = row.buyer?.company || row.buyer?.name || email;
  const openAR = row.offer?.used || 0;
  const lastOrder = row.buyer?.last_order_at_human || (row.status === "awaiting" || row.status === "invited" || row.status === "revoked" ? "—" : "—");
  const tinted = row.risk ? "bg-rose-50/30" : "";
  return (
    <div
      className={`grid grid-cols-[2fr_120px_70px_120px_110px_140px_110px_140px] gap-4 px-5 py-3 items-center hover:bg-secondary/30 transition-colors ${row.buyer ? "cursor-pointer" : ""} ${tinted}`}
      onClick={row.buyer ? onOpen : undefined}
      data-testid={`buyer-row-${row.id}`}
    >
      <div className="min-w-0">
        <div className="text-[13.5px] font-medium truncate flex items-center gap-1.5">
          {displayName}
          {row.buyer?.buyer_number && (
            <span
              data-testid={`buyer-number-${row.id}`}
              title="Customer # — your sequential ID for this buyer"
              className="text-[10px] mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-foreground/[0.06] text-foreground/65 font-normal flex-shrink-0"
            >#{row.buyer.buyer_number}</span>
          )}
          {row.connection?.insured && <ShieldCheck className="w-3 h-3 text-emerald-600" title="Insured"/>}
          <AllianzGradeChip buyer={row.buyer}/>
          <CoverChip buyer={row.buyer}/>
        </div>
        <div className="text-[11.5px] text-foreground/55 truncate">{email}</div>
      </div>
      <div><StatusPill status={row.status}/></div>
      <div className="flex justify-center"><ScoreBadge score={row.score}/></div>
      <div><RiskChip risk={row.risk}/></div>
      <div className="text-[13px] font-medium">{openAR ? money(openAR) : <span className="text-foreground/40">—</span>}</div>
      <div><InlineTermsCell row={row} onChange={onChangeTerms}/></div>
      <div className="text-[12.5px] text-foreground/60">{lastOrder}</div>

      <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
        {row.status === "awaiting" && row.connection && (
          <>
            <button
              onClick={() => onApprove(row.connection.id)}
              data-testid={`approve-${row.id}`}
              className="text-[11.5px] mono uppercase tracking-wider px-2.5 py-1 rounded-md bg-emerald-600 text-white hover:bg-emerald-700"
            >Approve</button>
            <button
              onClick={() => onDecline(row.connection.id)}
              data-testid={`decline-${row.id}`}
              className="text-[11.5px] mono uppercase tracking-wider px-2.5 py-1 rounded-md border border-rose-200 text-rose-700 hover:bg-rose-50"
            >Decline</button>
          </>
        )}
        {row.status === "invited" && row.invite && (
          <>
            <button
              onClick={() => onResend(row.invite.id)}
              data-testid={`resend-${row.id}`}
              className="text-[11.5px] mono uppercase tracking-wider px-2.5 py-1 rounded-md border border-border hover:bg-secondary inline-flex items-center gap-1"
            ><RefreshCw className="w-3 h-3"/>Resend</button>
            <RowMenu testid={`menu-${row.id}`} items={[
              { label: "Set terms", icon: Pencil, onClick: () => onGrant(row.invite.email) },
              { label: "Revoke invite", icon: Trash2, onClick: () => onRevokeInvite(row.invite.id), danger: true },
            ]}/>
          </>
        )}
        {row.status === "connected" && row.buyer && (
          <RowMenu testid={`menu-${row.id}`} items={[
            ...(row.offer ? [{ label: "Edit terms", icon: Pencil, onClick: () => onEdit(row.offer) }] : [{ label: "Set terms", icon: Pencil, onClick: () => onGrant(row.buyer.email) }]),
            { label: "Insure", icon: ShieldCheck, onClick: () => onInsure(row.buyer.id) },
            { label: "Message", icon: MessageCircle, onClick: () => onMessage(row.buyer.id) },
            ...(row.offer ? [{ label: "Revoke policy", icon: Trash2, onClick: () => onRevokeOffer(row.offer), danger: true }] : []),
          ]}/>
        )}
      </div>
    </div>
  );
}

function RowMenu({ items, testid }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    setTimeout(() => document.addEventListener("click", handler), 0);
    return () => document.removeEventListener("click", handler);
  }, [open]);
  return (
    <div className="relative" ref={ref}>
      <button
        onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}
        data-testid={testid}
        className="p-1 hover:bg-secondary rounded-md"
      ><MoreHorizontal className="w-4 h-4 text-foreground/50"/></button>
      {open && (
        <div className="absolute top-full right-0 mt-1 z-20 rounded-xl border border-border bg-white shadow-lg p-1 min-w-[180px]">
          {items.map((it, i) => (
            <button
              key={i}
              onClick={() => { it.onClick(); setOpen(false); }}
              className={`w-full text-left px-3 py-2 rounded-lg text-[13px] inline-flex items-center gap-2 ${it.danger ? "text-rose-700 hover:bg-rose-50" : "hover:bg-secondary"}`}
            >
              <it.icon className="w-3.5 h-3.5"/>{it.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function SingleInviteDialog({ open, onClose, onDone }) {
  const [form, setForm] = useState({ email: "", default_terms: "NET_30", additional_credit_limit: 0, note: "" });
  const [lookup, setLookup] = useState(null);
  const [saving, setSaving] = useState(false);
  const [looking, setLooking] = useState(false);
  const [profileGap, setProfileGap] = useState(null); // { tenant_slug } when profile is incomplete
  // Buyer-add SaaS cost impact (Phase 3-e · Jun 2026)
  // null while loading · {subscribed:false} for legacy/unsubscribed sellers
  // · {subscribed:true, ...} with full breakdown otherwise.
  const [impact, setImpact] = useState(null);
  const [overageConfirm, setOverageConfirm] = useState(null); // { delta_cents, plan_name, ... } when backend gates the request

  // Reset when reopened.
  useEffect(() => {
    if (open) {
      setForm({ email: "", default_terms: "NET_30", additional_credit_limit: 0, note: "" });
      setLookup(null);
      setOverageConfirm(null);
      // Check profile completeness — non-blocking nudge if barebones.
      api.get("/tenant/me").then(r => {
        const t = r.data || {};
        const p = t.settings?.profile || {};
        const isIncomplete = !p.about || !(p.categories && p.categories.length);
        setProfileGap(isIncomplete ? { tenant_slug: t.slug } : null);
      }).catch(() => setProfileGap(null));
      // Pull SaaS buyer-add impact for the inline counter + first-time nudge.
      api.get("/billing/buyer-add-impact")
        .then(r => setImpact(r.data))
        .catch(() => setImpact({ subscribed: false }));
    }
  }, [open]);

  // Lookup score on email blur.
  const onEmailBlur = async () => {
    const e = form.email.trim().toLowerCase();
    if (!e || !e.includes("@")) return;
    setLooking(true);
    try {
      const { data } = await api.get(`/tenant/lookup-buyer?email=${encodeURIComponent(e)}`);
      setLookup(data);
      if (data.on_verqo) {
        setForm(f => ({ ...f, default_terms: data.suggested_terms || "NET_30" }));
      }
    } catch {} finally { setLooking(false); }
  };

  const submit = async (e, opts = {}) => {
    if (e?.preventDefault) e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      const payload = {
        email: form.email.trim().toLowerCase(),
        default_terms: form.default_terms,
        additional_credit_limit: Number(form.additional_credit_limit) || 0,
        note: form.note || null,
      };
      if (opts.acknowledged) payload.overage_acknowledged = true;
      await api.post("/tenant/invites", payload);
      // Auto-ack the first-time nudge so it stops nagging the seller.
      if (impact?.first_overage && !impact?.overage_nudge_seen) {
        try { await api.post("/billing/overage-nudge/ack"); } catch {}
      }
      toast.success("Invite sent");
      onDone();
    } catch (err) {
      // Backend returned 402 overage_approval_required → open confirm modal
      // instead of toasting.
      const det = err.response?.data?.detail;
      if (det && det.code === "overage_approval_required") {
        setOverageConfirm(det);
        setSaving(false);
        return;
      }
      errorToastFromAxios(err, "Could not send invite", {
        subject: `Couldn't invite buyer ${form.email}`,
        body:
          `I tried to invite ${form.email} as a buyer and got an error.\n` +
          `Default terms: ${form.default_terms}\n` +
          `Additional credit limit: ${money(Number(form.additional_credit_limit) || 0)}`,
        urgency: "normal",
        category: "invites",
      });
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="single-invite-dialog">
        <DialogHeader><DialogTitle>Invite a buyer</DialogTitle></DialogHeader>

        {profileGap && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 mt-2 text-[12.5px] text-amber-900" data-testid="invite-profile-gap-nudge">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0"/>
              <div className="flex-1">
                <strong>Your public profile is barely filled in.</strong> Buyers vet sellers via the profile before accepting — sellers with a complete one see higher accept rates.
                <div className="mt-1.5">
                  <Link to="/seller/settings#public-profile" onClick={onClose} className="underline underline-offset-2 hover:text-amber-950" data-testid="invite-profile-gap-cta">
                    Complete profile first →
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={submit} className="space-y-3 mt-2">
          <div>
            <Label className="text-xs mono uppercase tracking-wider text-foreground/55">Buyer email</Label>
            <Input
              type="email"
              required
              value={form.email}
              onChange={e => setForm({ ...form, email: e.target.value })}
              onBlur={onEmailBlur}
              data-testid="invite-email"
              className="mt-1.5 h-11"
              placeholder="buyer@store.com"
            />
            {looking && <div className="mt-1.5 text-[11px] text-foreground/50">Looking up verqo score…</div>}
            {lookup?.on_verqo && (
              <div className="mt-1.5 text-[11.5px] inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200" data-testid="invite-score-pill">
                <Sparkles className="w-3 h-3"/>
                {lookup.label} · Score {lookup.score} · Suggested terms <strong>{lookup.suggested_terms.replace("NET_", "Net-")}</strong>
              </div>
            )}
            {lookup && lookup.on_verqo === false && (
              <div className="mt-1.5 text-[11px] text-foreground/55">Not on verqo yet — invite will activate when they sign up.</div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs mono uppercase tracking-wider text-foreground/55">Default terms</Label>
              <select
                value={form.default_terms}
                onChange={e => setForm({ ...form, default_terms: e.target.value })}
                data-testid="invite-terms"
                className="mt-1.5 w-full h-11 rounded-md border border-border bg-white px-3 text-[13.5px]"
              >
                {["NET_14", "NET_30", "NET_60", "NET_90", "PREPAID"].map(t => <option key={t} value={t}>{t.replace("NET_", "Net-")}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs mono uppercase tracking-wider text-foreground/55">Extra credit ($)</Label>
              <Input
                type="number" min="0"
                value={form.additional_credit_limit}
                onChange={e => setForm({ ...form, additional_credit_limit: e.target.value })}
                data-testid="invite-credit"
                className="mt-1.5 h-11"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs mono uppercase tracking-wider text-foreground/55">Note (optional)</Label>
            <Input
              value={form.note}
              onChange={e => setForm({ ...form, note: e.target.value })}
              data-testid="invite-note"
              className="mt-1.5 h-11"
              placeholder="What should they know?"
            />
          </div>
          <div className="flex gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1 h-11 rounded-full">Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="invite-submit" className="flex-1 h-11 rounded-full bg-foreground text-background hover:bg-foreground/90">
              <Send className="w-3.5 h-3.5 mr-1.5"/>{saving ? "Sending…" : "Send invite"}
            </Button>
          </div>

          {/* ── SaaS billing inline counter (Phase 3-e · Jun 2026) ─────────
              Lives at the bottom of the form, ABOVE the action buttons, so
              the seller sees the bill delta in their peripheral vision but
              isn't blocked by it. Only renders for subscribed sellers. */}
          {impact?.subscribed && (
            <div data-testid="invite-bill-impact" className="text-[12px] text-foreground/55 flex items-center justify-between pt-1.5 -mb-1">
              <span>
                After this invite: <span className="text-foreground font-medium">{impact.active_buyers_this_cycle + 1}</span>
                <span className="text-foreground/40"> / {impact.buyers_included} included</span>
              </span>
              <span>
                Next bill:{" "}
                <span className="text-foreground font-medium">
                  ${(impact.with_one_more_bill_cents / 100).toLocaleString(undefined, {maximumFractionDigits:0})}
                </span>
                {impact.delta_cents > 0 && (
                  <span className="text-amber-700"> (+${(impact.delta_cents/100).toLocaleString(undefined,{maximumFractionDigits:0})})</span>
                )}
              </span>
            </div>
          )}

          {/* First-time overage nudge — only when crossing included→overage
              for the FIRST time AND the seller hasn't acknowledged. */}
          {impact?.first_overage && !impact?.overage_nudge_seen && (
            <div data-testid="invite-first-overage-nudge"
                 className="mt-1 rounded-lg border border-emerald-300 bg-emerald-50 p-3 text-[12.5px] text-emerald-900 flex items-start gap-2">
              <Sparkles className="w-4 h-4 mt-0.5 shrink-0 text-emerald-700"/>
              <div>
                <strong>Heads up — this is your first buyer beyond your {impact.plan_name} plan&apos;s {impact.buyers_included} included.</strong>
                {" "}Each additional one auto-debits ${(impact.per_buyer_rate_cents/100).toLocaleString(undefined,{maximumFractionDigits:0})}/mo from your wallet.{" "}
                <Link to="/seller/settings#plan-billing" onClick={onClose} className="underline underline-offset-2 hover:text-emerald-950">
                  Upgrade for more included →
                </Link>
              </div>
            </div>
          )}
        </form>
      </DialogContent>

      {/* ── Hard-gate confirm modal (opt-in via Settings → Plan & Billing) ──
          Renders only when the backend returns 402 overage_approval_required
          (i.e. the seller flipped the "ask me first" toggle). Two clicks
          minimum: open Settings to fix the toggle OR confirm the charge. */}
      <Dialog open={!!overageConfirm} onOpenChange={(v) => !v && setOverageConfirm(null)}>
        <DialogContent className="max-w-md" data-testid="overage-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Confirm new buyer charge</DialogTitle>
          </DialogHeader>
          {overageConfirm && (
            <div className="space-y-3 text-[13.5px]">
              <p className="text-foreground/75">{overageConfirm.message}</p>
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-[12.5px] text-amber-900">
                Adding <strong>{form.email}</strong> bumps you to{" "}
                <strong>{(overageConfirm.active_now ?? 0) + 1}</strong> active buyers on{" "}
                <strong>{overageConfirm.plan_name}</strong> ({overageConfirm.buyers_included} included).
              </div>
            </div>
          )}
          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={() => setOverageConfirm(null)} className="flex-1 h-11 rounded-full" data-testid="overage-cancel">
              Cancel
            </Button>
            <Button
              onClick={() => { setOverageConfirm(null); submit(null, { acknowledged: true }); }}
              className="flex-1 h-11 rounded-full bg-foreground text-background hover:bg-foreground/90"
              data-testid="overage-confirm-proceed">
              Confirm &amp; invite
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}

function BulkInviteDialog({ open, onClose, onDone }) {
  const [text, setText] = useState("");
  const [terms, setTerms] = useState("NET_30");
  const [credit, setCredit] = useState(0);
  const [saving, setSaving] = useState(false);
  const [results, setResults] = useState(null);
  const emails = useMemo(() =>
    text.split(/[\s,;]+/).map(s => s.trim()).filter(Boolean),
  [text]);

  useEffect(() => { if (open) { setText(""); setTerms("NET_30"); setCredit(0); setResults(null); } }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!emails.length) return toast.error("Paste at least one email");
    setSaving(true);
    try {
      const { data } = await api.post("/tenant/invites/bulk", {
        emails, default_terms: terms, additional_credit_limit: Number(credit) || 0,
      });
      setResults(data);
      toast.success(`${data.summary.invited} invited · ${data.summary.skipped} skipped`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk invite failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="bulk-invite-dialog">
        <DialogHeader><DialogTitle>Bulk invite buyers</DialogTitle></DialogHeader>
        {!results && (
          <form onSubmit={submit} className="space-y-3 mt-2">
            <div>
              <Label className="text-xs mono uppercase tracking-wider text-foreground/55">Paste emails (one per line, comma- or space-separated)</Label>
              <textarea
                value={text}
                onChange={e => setText(e.target.value)}
                rows={6}
                data-testid="bulk-emails"
                placeholder="alice@brand.com&#10;bob@store.com,carol@shop.io"
                className="mt-1.5 w-full rounded-md border border-border bg-white p-3 text-[13.5px] focus:outline-none focus:ring-2 focus:ring-foreground/10 resize-none"
              />
              <div className="mt-1.5 text-[11px] text-foreground/55">{emails.length} email{emails.length !== 1 ? "s" : ""} detected</div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs mono uppercase tracking-wider text-foreground/55">Default terms</Label>
                <select
                  value={terms}
                  onChange={e => setTerms(e.target.value)}
                  data-testid="bulk-terms"
                  className="mt-1.5 w-full h-11 rounded-md border border-border bg-white px-3 text-[13.5px]"
                >
                  {["NET_14", "NET_30", "NET_60", "NET_90", "PREPAID"].map(t => <option key={t} value={t}>{t.replace("NET_", "Net-")}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs mono uppercase tracking-wider text-foreground/55">Extra credit per buyer ($)</Label>
                <Input
                  type="number" min="0"
                  value={credit}
                  onChange={e => setCredit(e.target.value)}
                  data-testid="bulk-credit"
                  className="mt-1.5 h-11"
                />
              </div>
            </div>
            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" onClick={onClose} className="flex-1 h-11 rounded-full">Cancel</Button>
              <Button type="submit" disabled={saving || !emails.length} data-testid="bulk-submit" className="flex-1 h-11 rounded-full bg-foreground text-background hover:bg-foreground/90">
                {saving ? "Sending…" : `Invite ${emails.length}`}
              </Button>
            </div>
          </form>
        )}
        {results && (
          <div className="space-y-3 mt-2">
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-[13px] text-emerald-900">
              <strong>{results.summary.invited}</strong> invited · <strong>{results.summary.skipped}</strong> skipped
            </div>
            <div className="rounded-xl border border-border bg-white max-h-64 overflow-y-auto divide-y divide-border" data-testid="bulk-results">
              {results.results.map((r, i) => (
                <div key={i} className="px-3 py-2 flex items-center justify-between text-[13px]">
                  <span className="truncate">{r.email}</span>
                  <span className={`text-[10.5px] mono uppercase tracking-wider px-2 py-0.5 rounded-full border ${
                    r.status === "invited" ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                      : r.reason === "already_connected" ? "bg-foreground/5 text-foreground/55 border-foreground/15"
                      : "bg-amber-50 text-amber-700 border-amber-200"
                  }`}>{r.status === "invited" ? "Invited" : (r.reason || "Skipped").replace(/_/g, " ")}</span>
                </div>
              ))}
            </div>
            <div className="flex justify-end pt-2">
              <Button onClick={onDone} className="h-11 rounded-full bg-foreground text-background hover:bg-foreground/90 px-6">Done</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// Local helper — extracts the existing Grant dialog into its own
// component so the main return is readable. The body is a verbatim
// copy of what was there before, just parameterized.
function GrantDialog({ open, onOpenChange, editing, form, setForm, buyerByEmail, onSubmit, onCancel }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="grant-dialog">
        <DialogHeader>
          <DialogTitle className="display text-2xl tracking-tight">
            {editing ? "Edit payment policy" : "Set payment policy"}
          </DialogTitle>
        </DialogHeader>
        <div className="mt-1 space-y-4">
          <div>
            <Label className="text-xs mono uppercase tracking-[0.18em] text-foreground/50">Buyer email</Label>
            <div className="relative mt-1.5">
              <Mail className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-foreground/40" />
              <Input
                value={form.buyer_email}
                onChange={e => !editing && setForm({ ...form, buyer_email: e.target.value })}
                disabled={!!editing}
                placeholder="buyer@store.com"
                data-testid="grant-email"
                className="h-11 pl-9"
              />
            </div>
            {!editing && form.buyer_email && !buyerByEmail[form.buyer_email.toLowerCase()] && (
              <div className="mt-1.5 text-[11px] text-foreground/55 inline-flex items-center gap-1.5">
                <AlertCircle className="w-3 h-3" />
                Not on verqo yet — policy activates the moment they sign up with this email.
              </div>
            )}
          </div>

          <div>
            <Label className="text-xs mono uppercase tracking-[0.18em] text-foreground/50">Terms</Label>
            <div className="mt-1.5 grid grid-cols-3 gap-1.5">
              {Object.entries(TERMS_META).map(([k, v]) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setForm({ ...form, terms: k })}
                  data-testid={`grant-terms-${k}`}
                  className={`px-2.5 py-2 rounded-md border text-left transition-colors ${form.terms === k ? "border-foreground bg-foreground text-background" : "border-border hover:border-foreground/40"}`}
                >
                  <div className="text-[13px] font-medium">{v.label}</div>
                  <div className={`text-[10.5px] mt-0.5 ${form.terms === k ? "text-background/70" : "text-foreground/55"}`}>{v.sub}</div>
                </button>
              ))}
            </div>
          </div>

          {form.terms !== "prepaid" && (
            <div>
              <Label className="text-xs mono uppercase tracking-[0.18em] text-foreground/50">Credit limit ($)</Label>
              <Input
                type="number" min="0" step="1000"
                value={form.limit}
                onChange={e => setForm({ ...form, limit: e.target.value })}
                data-testid="grant-limit"
                className="mt-1.5 h-11"
              />
            </div>
          )}

          <div>
            <Label className="text-xs mono uppercase tracking-[0.18em] text-foreground/50">Note (optional)</Label>
            <Input
              value={form.note}
              onChange={e => setForm({ ...form, note: e.target.value })}
              data-testid="grant-note"
              className="mt-1.5 h-11"
              placeholder="Internal note (visible only to you)"
            />
          </div>

          {form.terms !== "prepaid" && (
            <AllianzQuoteCallout
              buyerId={buyerByEmail[(form.buyer_email || "").toLowerCase()]?.id}
              limit={Number(form.limit) || 0}
            />
          )}

          <div className="flex gap-2 pt-2">
            <Button
              variant="outline"
              type="button"
              onClick={onCancel}
              className="flex-1 h-11 rounded-full"
            >Cancel</Button>
            <Button
              onClick={onSubmit}
              data-testid="grant-submit"
              className="flex-1 h-11 rounded-full bg-foreground text-background hover:bg-foreground/90"
            >
              {editing ? "Save changes" : form.terms === "prepaid" ? "Set to Prepay only" : `Grant ${money(Number(form.limit) || 0)}`}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function KPI({ label, value, hint }) {
  return (
    <div className="rounded-2xl border border-border bg-white p-4">
      <div className="text-[10px] mono uppercase tracking-[0.18em] text-foreground/50">{label}</div>
      <div className="display text-2xl mt-1.5 tracking-tight">{value}</div>
      {hint && <div className="text-[11px] text-foreground/55 mt-1 truncate">{hint}</div>}
    </div>
  );
}

// Allianz Trade pre-flight quote — non-binding "they'll likely cover this for
// $X at Y%/yr" callout that appears in the grant-terms dialog the moment a
// seller is about to extend credit to a buyer. Backed by /insurance/v2/allianz/quote
// which combines the buyer's persisted Allianz grade with a coverage factor.
// Renders nothing until the buyer is on verqo + has a positive limit.
function AllianzQuoteCallout({ buyerId, limit }) {
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!buyerId || !limit || limit <= 0) { setQuote(null); return; }
    let alive = true;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/insurance/v2/allianz/quote", {
          buyer_id: buyerId, requested_limit: Number(limit),
        });
        if (alive) setQuote(data);
      } catch { if (alive) setQuote(null); }
      finally { if (alive) setLoading(false); }
    }, 350);
    return () => { alive = false; clearTimeout(t); };
  }, [buyerId, limit]);

  if (!buyerId) return null;
  if (loading && !quote) {
    return (
      <div className="rounded-xl border border-foreground/10 bg-secondary/30 px-3.5 py-2.5 text-[11.5px] text-foreground/55 inline-flex items-center gap-2" data-testid="allianz-quote-loading">
        <Shield className="w-3.5 h-3.5 animate-pulse"/>Checking with Allianz Trade…
      </div>
    );
  }
  if (!quote) return null;
  if (!quote.grade) {
    return (
      <div className="rounded-xl border border-foreground/15 bg-foreground/5 px-3.5 py-2.5 text-[11.5px] text-foreground/60 flex items-center gap-2" data-testid="allianz-quote-na">
        <Shield className="w-3.5 h-3.5"/>
        Allianz Trade has no record for this buyer yet — credit will be extended on verqo Trade Score only.
      </div>
    );
  }
  const accepted = quote.decision === "accepted" && quote.likely_limit > 0;
  const cls = accepted
    ? "border-emerald-200 bg-emerald-50/50 text-emerald-900"
    : "border-rose-200 bg-rose-50/50 text-rose-900";
  return (
    <div className={`rounded-xl border ${cls} px-3.5 py-3`} data-testid="allianz-quote-callout">
      <div className="flex items-start gap-3">
        <Shield className={`w-4 h-4 mt-0.5 shrink-0 ${accepted ? "text-emerald-600" : "text-rose-600"}`}/>
        <div className="flex-1 text-[12px] leading-snug">
          {accepted ? (
            <>
              <div>
                <strong>Allianz will likely cover this for {money(quote.likely_limit)}</strong> at <strong>{(quote.premium_rate * 100).toFixed(2)}%/yr</strong> ({money(quote.annual_premium)} annual premium).
              </div>
              <div className="text-[11px] mt-0.5 opacity-70">
                Grade <span className="mono uppercase">{quote.grade}</span> · {quote.outlook || "stable"}
                {quote.coverage_factor < 1 && <> · Allianz typically grants {Math.round(quote.coverage_factor * 100)}% of requested</>}
                {quote.is_mock && " · simulated (live keys pending)"}
              </div>
            </>
          ) : (
            <>
              <div><strong>Allianz unlikely to cover</strong> — grade <span className="mono uppercase">{quote.grade}</span>.</div>
              <div className="text-[11px] mt-0.5 opacity-70">You can still extend credit on your own balance sheet, but it won't be insured.</div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function PolicyBadge({ offer }) {
  if (!offer) {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] text-foreground/45 mono uppercase tracking-[0.14em]">
        <span className="w-1.5 h-1.5 rounded-full bg-foreground/30"/>
        No policy
      </span>
    );
  }
  if (offer.terms === "prepaid") {
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-rose-50 text-rose-700 border border-rose-200 text-[11px] font-medium">
        <Ban className="w-3 h-3"/>Prepay only
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 text-[11px] font-medium">
      <ShieldCheck className="w-3 h-3"/>
      {TERMS_META[offer.terms]?.label || offer.terms} · {money(offer.limit)}
    </span>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// BuyerCustomerNumberChip — editable Customer # chip on the buyer drawer header.
// Click to edit inline; saves to PATCH /api/seller/buyers/{bid}/number.
// ───────────────────────────────────────────────────────────────────────────
function BuyerCustomerNumberChip({ buyerId, initial, onUpdated }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue]     = useState(initial != null ? String(initial) : "");
  const [saving, setSaving]   = useState(false);

  useEffect(() => { setValue(initial != null ? String(initial) : ""); }, [initial]);

  const save = async (e) => {
    e?.preventDefault?.();
    const n = parseInt(value, 10);
    if (!Number.isFinite(n) || n <= 0) {
      toast.error("Customer # must be a positive integer");
      return;
    }
    if (n === initial) { setEditing(false); return; }
    setSaving(true);
    try {
      const r = await api.patch(`/seller/buyers/${buyerId}/number`, { number: n });
      onUpdated?.(r.data.buyer_number);
      toast.success(`Customer # set to ${r.data.buyer_number}`);
      setEditing(false);
    } catch (err) {
      toast.error(err.response?.data?.detail?.message || err.response?.data?.detail || "Couldn't save");
    } finally { setSaving(false); }
  };

  if (editing) {
    return (
      <form onSubmit={save} className="inline-flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
        <span className="text-[11px] mono uppercase tracking-wider text-foreground/55">Customer&nbsp;#</span>
        <input
          autoFocus
          value={value}
          onChange={(e) => setValue(e.target.value.replace(/[^0-9]/g, ""))}
          onBlur={save}
          onKeyDown={(e) => { if (e.key === "Escape") { setValue(initial != null ? String(initial) : ""); setEditing(false); } }}
          data-testid="buyer-number-input"
          inputMode="numeric"
          maxLength={9}
          className="w-20 h-6 text-[12.5px] mono px-1.5 border border-foreground/30 rounded bg-white outline-none focus:border-foreground"
        />
        {saving && <span className="text-[10px] mono text-foreground/50">…</span>}
      </form>
    );
  }
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); setEditing(true); }}
      data-testid="drawer-buyer-number"
      title={initial ? "Click to edit this buyer's customer #" : "Click to assign a customer #"}
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] mono uppercase tracking-wider border normal-case hover:bg-foreground/[0.04] transition-colors
                 bg-foreground/[0.04] text-foreground/70 border-foreground/15"
    >
      <Pencil className="w-2.5 h-2.5"/>
      Customer&nbsp;#{initial ?? "—"}
    </button>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// BuyerDetailDrawer — deep-dive panel. Opens when a seller clicks a buyer row.
// Shows: header (score + key stats), active policy, tabs for Overview /
// Orders / Invoices / Activity timeline.
// ─────────────────────────────────────────────────────────────────────────────
function BuyerDetailDrawer({ buyerId, onClose, onGrant, onEdit, onRevoke, onInsure }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("overview");
  const [insurance, setInsurance] = useState(null); // matching aggregate row, if any
  const [masterPolicy, setMasterPolicy] = useState(null);

  useEffect(() => {
    if (!buyerId) { setData(null); setInsurance(null); setMasterPolicy(null); return; }
    setLoading(true);
    setTab("overview");
    api.get(`/seller/buyers/${buyerId}/detail`)
      .then(r => setData(r.data))
      .catch(() => { toast.error("Could not load buyer"); onClose?.(); })
      .finally(() => setLoading(false));
    api.get("/insurance/v2/buyer-aggregate")
      .then(r => {
        const row = (r.data || []).find(x => x.buyer_id === buyerId);
        setInsurance(row || null);
      })
      .catch(() => setInsurance(null));
    // Master policy summary — shown so seller knows their aggregate coverage
    // umbrella status while reviewing a buyer.
    api.get("/seller/master-policy/status")
      .then(r => setMasterPolicy(r.data || null))
      .catch(() => setMasterPolicy(null));
  }, [buyerId, onClose]);

  const open = !!buyerId;

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose?.()}>
      <SheetContent side="right" className="w-full sm:max-w-[640px] p-0 overflow-y-auto" data-testid="buyer-detail-drawer">
        {/* Always-rendered title for Radix a11y — visually hidden, replaced by the real title once data loads. */}
        <SheetTitle className="sr-only">Buyer detail</SheetTitle>
        {loading && !data && (
          <div className="p-10 text-center text-sm text-foreground/50">Loading buyer 360°…</div>
        )}
        {data && (
          <div>
            {/* Header */}
            <SheetHeader className="px-6 pt-6 pb-5 border-b border-border">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-[10px] mono uppercase tracking-[0.2em] text-foreground/50">Buyer 360°</div>
                  <div className="display text-2xl tracking-tight mt-1 truncate flex items-center gap-2 flex-wrap" data-testid="drawer-buyer-name">
                    <span className="truncate">{data.buyer.company || data.buyer.name}</span>
                    {data.buyer.ein && (
                      <span data-testid="drawer-kyb-chip"
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] mono uppercase tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-200 normal-case">
                        <Check className="w-2.5 h-2.5"/>KYB
                      </span>
                    )}
                    <BuyerCustomerNumberChip
                      buyerId={data.buyer.id}
                      initial={data.buyer.buyer_number}
                      onUpdated={(n) => setData((d) => ({...d, buyer: {...d.buyer, buyer_number: n}}))}
                    />
                  </div>
                  {/* Identity meta row — surfaces business type / location / age
                      so the seller can vet the buyer at a glance without
                      hunting through individual fields. */}
                  <div className="text-[12px] text-foreground/55 mt-1 flex items-center gap-2 flex-wrap">
                    {data.buyer.business_type && (
                      <span className="inline-flex items-center gap-1"><Building2 className="w-3 h-3"/>{data.buyer.business_type}</span>
                    )}
                    {(data.buyer.address?.city || data.buyer.address?.state) && (
                      <>
                        <span>·</span>
                        <span className="inline-flex items-center gap-1">
                          <MapPin className="w-3 h-3"/>
                          {[data.buyer.address?.city, data.buyer.address?.state].filter(Boolean).join(", ")}
                        </span>
                      </>
                    )}
                    {data.buyer.founded_year && (
                      <>
                        <span>·</span>
                        <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3"/>Est. {data.buyer.founded_year}</span>
                      </>
                    )}
                  </div>
                  <div className="text-[12px] text-foreground/55 truncate mt-0.5">
                    {data.buyer.name} · {data.buyer.email}
                  </div>
                </div>
                <button onClick={onClose} data-testid="close-drawer" className="w-8 h-8 rounded-full hover:bg-secondary grid place-items-center text-foreground/50">
                  <X className="w-4 h-4"/>
                </button>
              </div>

              {/* ── Company overview · Jun 2026 ─────────────────────
                  Surfaces identity info sellers couldn't see before:
                  category, website, contact, social handles, what
                  the buyer buys. Gracefully omits rows when the buyer
                  hasn't filled in their profile yet. Sits ABOVE the
                  score/stats so the seller sees "who is this" before
                  "how creditworthy". */}
              {(() => {
                const b = data.buyer || {};
                const hasWebsite = !!b.website;
                const hasCats = Array.isArray(b.categories) && b.categories.length > 0;
                const hasSocials = b.social && (b.social.linkedin || b.social.instagram || b.social.twitter);
                const hasContactExtra = b.email || b.phone;
                const hasBrandFit = b.store_type || b.revenue_band || b.geographic_reach;
                // Don't render the block at all if we'd show a single
                // line — keeps the drawer clean for sparse buyer profiles.
                const richEnough = hasWebsite || hasCats || hasSocials || hasContactExtra || hasBrandFit;
                if (!richEnough) return null;
                return (
                  <div data-testid="drawer-company-overview"
                       className="mt-4 rounded-xl border border-foreground/10 bg-secondary/30 px-4 py-3 text-[12px] space-y-1.5">
                    <div className="text-[9.5px] mono uppercase tracking-[0.18em] text-foreground/55 mb-1">
                      Company overview
                    </div>
                    {hasCats && (
                      <div className="flex items-start gap-2">
                        <span className="text-foreground/55 w-20 shrink-0">Category</span>
                        <div className="flex items-center gap-1.5 flex-wrap">
                          {b.categories.slice(0, 6).map(c => (
                            <span key={c} className="chip bg-background border border-foreground/10 text-[11px]">{c}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    {/* Jun 2026 — buyer-side Public Profile fields */}
                    {b.store_type && (
                      <div className="flex items-start gap-2">
                        <span className="text-foreground/55 w-20 shrink-0">Store type</span>
                        <span className="text-foreground/85">{({
                          boutique: "Boutique",
                          specialty: "Specialty retailer",
                          dept_store: "Department store",
                          chain: "Chain",
                          online_only: "Online-only",
                          distributor: "Wholesale distributor",
                          spa_pro: "Spa / professional",
                        })[b.store_type] || b.store_type}</span>
                      </div>
                    )}
                    {b.revenue_band && (
                      <div className="flex items-start gap-2">
                        <span className="text-foreground/55 w-20 shrink-0">Revenue</span>
                        <span className="text-foreground/85">{({
                          under_500k: "Under $500K / yr",
                          "500k_5m":   "$500K – $5M / yr",
                          "5m_plus":   "$5M+ / yr",
                        })[b.revenue_band] || b.revenue_band}<span className="text-foreground/45 text-[11px] ml-1">estimated</span></span>
                      </div>
                    )}
                    {b.geographic_reach && (
                      <div className="flex items-start gap-2">
                        <span className="text-foreground/55 w-20 shrink-0">Reach</span>
                        <span className="text-foreground/85">{({
                          single: "Single location",
                          regional: "Regional",
                          national: "National",
                          international: "International",
                        })[b.geographic_reach] || b.geographic_reach}</span>
                      </div>
                    )}
                    {hasWebsite && (
                      <div className="flex items-start gap-2">
                        <span className="text-foreground/55 w-20 shrink-0">Website</span>
                        <a href={/^https?:\/\//.test(b.website) ? b.website : `https://${b.website}`}
                           target="_blank" rel="noreferrer"
                           data-testid="drawer-website"
                           className="text-foreground/85 underline inline-flex items-center gap-1 truncate">
                          {b.website.replace(/^https?:\/\//, "")}
                          <ExternalLink className="w-2.5 h-2.5 opacity-65 shrink-0"/>
                        </a>
                      </div>
                    )}
                    {hasContactExtra && (
                      <div className="flex items-start gap-2">
                        <span className="text-foreground/55 w-20 shrink-0">Contact</span>
                        <div className="flex-1 min-w-0">
                          {b.email && (
                            <a href={`mailto:${b.email}`}
                               className="block text-[11.5px] text-foreground/65 underline truncate">
                              {b.email}
                            </a>
                          )}
                          {b.phone && (
                            <a href={`tel:${b.phone.replace(/\s/g, "")}`}
                               className="block text-[11.5px] text-foreground/65 inline-flex items-center gap-1">
                              <Phone className="w-2.5 h-2.5"/>{b.phone}
                              {b.phone_verified && (
                                <span className="inline-flex items-center gap-0.5 ml-1 px-1 py-0.5 rounded text-[9px] mono uppercase tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-200">
                                  <Check className="w-2 h-2"/>Verified
                                </span>
                              )}
                            </a>
                          )}
                        </div>
                      </div>
                    )}
                    {hasSocials && (
                      <div className="flex items-start gap-2">
                        <span className="text-foreground/55 w-20 shrink-0">Social</span>
                        <div className="flex items-center gap-3 flex-wrap">
                          {b.social?.linkedin && (
                            <a href={/^https?:\/\//.test(b.social.linkedin) ? b.social.linkedin : `https://${b.social.linkedin}`}
                               target="_blank" rel="noreferrer"
                               className="inline-flex items-center gap-1 text-[11.5px] text-foreground/65 hover:text-foreground">
                              <Linkedin className="w-3 h-3"/>LinkedIn<ExternalLink className="w-2.5 h-2.5 opacity-65"/>
                            </a>
                          )}
                          {b.social?.instagram && (
                            <a href={`https://instagram.com/${b.social.instagram.replace(/^@/, "")}`}
                               target="_blank" rel="noreferrer"
                               className="inline-flex items-center gap-1 text-[11.5px] text-foreground/65 hover:text-foreground">
                              <Instagram className="w-3 h-3"/>{b.social.instagram}<ExternalLink className="w-2.5 h-2.5 opacity-65"/>
                            </a>
                          )}
                          {b.social?.twitter && (
                            <a href={`https://twitter.com/${b.social.twitter.replace(/^@/, "")}`}
                               target="_blank" rel="noreferrer"
                               className="inline-flex items-center gap-1 text-[11.5px] text-foreground/65 hover:text-foreground">
                              {b.social.twitter}<ExternalLink className="w-2.5 h-2.5 opacity-65"/>
                            </a>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Score + stats row */}
              <div className="mt-5 grid grid-cols-4 gap-2">
                <div className={`rounded-xl border p-3 ${scoreBg(data.score.score)}`}>
                  <div className="text-[9px] mono uppercase tracking-wider text-foreground/55">verqo Trade Score</div>
                  <div className={`display text-2xl mt-0.5 ${scoreColor(data.score.score)}`}>{data.score.score}</div>
                  <div className="text-[9px] mono uppercase tracking-wider text-foreground/55 mt-0.5">{data.score.rating}</div>
                </div>
                <MiniStat label="Total volume · with you" value={money(data.stats.total_volume)}/>
                <MiniStat label="Paid · with you" value={money(data.stats.paid_volume)}/>
                <MiniStat label="Outstanding · with you" value={money(data.stats.outstanding)} tone={data.stats.outstanding > 0 ? "warn" : "default"}/>
              </div>

              {/* Active policy quick summary */}
              {(() => {
                const active = (data.offers || []).find(o => o.status === "active");
                if (!active) {
                  return (
                    <div className="mt-4 rounded-xl border border-dashed border-border bg-secondary/30 px-4 py-3 flex items-center justify-between gap-3">
                      <div className="text-[12px] text-foreground/60">No payment policy set for this buyer.</div>
                      <Button size="sm" onClick={() => onGrant?.(data.buyer.email)} data-testid="drawer-grant" className="rounded-full text-xs h-8 px-3">
                        <Plus className="w-3 h-3 mr-1"/>Set terms
                      </Button>
                    </div>
                  );
                }
                return (
                  <div className="mt-4 rounded-xl border border-border bg-white px-4 py-3 flex items-center justify-between gap-3" data-testid="drawer-active-policy">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <PolicyBadge offer={active}/>
                        {active.note && <span className="text-[11px] text-foreground/55 truncate">· {active.note}</span>}
                      </div>
                      {active.terms !== "prepaid" && (
                        <div className="text-[11px] text-foreground/55 mono mt-1">
                          {money(active.used)} used · {money(active.available)} available
                        </div>
                      )}
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <button onClick={() => onEdit?.(active)} data-testid="drawer-edit" className="px-2.5 py-1 rounded-full text-[11px] border border-border hover:border-foreground/40 hover:bg-secondary/40 inline-flex items-center gap-1">
                        <Pencil className="w-2.5 h-2.5"/>Edit
                      </button>
                      <button onClick={() => onRevoke?.(active)} data-testid="drawer-revoke" className="w-7 h-7 rounded-full hover:bg-rose-50 hover:text-rose-700 grid place-items-center text-foreground/60">
                        <Trash2 className="w-3 h-3"/>
                      </button>
                    </div>
                  </div>
                );
              })()}

              {/* Insurance · Allianz section */}
              <InsuranceSection
                insurance={insurance}
                buyer={data.buyer}
                onRequest={() => onInsure?.(data.buyer.id)}
              />

              {/* Phase SC-2 · Auto-fund this buyer's invoices */}
              <AutoFundQuickToggle buyerId={data.buyer.id} buyerCompany={data.buyer.company || data.buyer.name}/>
            </SheetHeader>

            {/* Tags + Statement action strip */}
            <BuyerTagsAndStatement buyerId={data.buyer.id}/>

            {/* Tabs */}
            <div className="px-6 pt-4 border-b border-border">
              <div className="flex gap-1" data-testid="drawer-tabs">
                {[
                  { k: "overview", l: "Overview", icon: TrendingUp },
                  { k: "orders",   l: `Orders (${data.orders.length})`, icon: ShoppingBag },
                  { k: "invoices", l: `Invoices (${data.invoices.length})`, icon: Receipt },
                  { k: "activity", l: "Activity", icon: Activity },
                ].map(t => (
                  <button
                    key={t.k}
                    onClick={() => setTab(t.k)}
                    data-testid={`drawer-tab-${t.k}`}
                    className={`px-3.5 py-2.5 text-sm border-b-2 transition-colors inline-flex items-center gap-1.5 ${tab === t.k ? "border-foreground text-foreground font-medium" : "border-transparent text-foreground/55 hover:text-foreground"}`}
                  >
                    <t.icon className="w-3.5 h-3.5"/>{t.l}
                  </button>
                ))}
              </div>
            </div>

            {/* Tab content */}
            <div className="p-6">
              {/* Master policy umbrella banner — visible only when seller has
                  applied. Surfaces the aggregate coverage umbrella covering
                  THIS buyer (and every other one). Feb 2026 — wired to the
                  new Quote/Pay flow. */}
              {masterPolicy && ["quoted", "awaiting_payment", "active"].includes(masterPolicy.status) && (
                <div className="mb-4 rounded-xl border border-border bg-secondary/30 px-4 py-3 flex items-center gap-3" data-testid="buyer-drawer-master-policy">
                  <ShieldCheck className="w-4 h-4 text-emerald-700 shrink-0" />
                  <div className="flex-1 text-[12.5px]">
                    <div className="font-medium">
                      {masterPolicy.status === "active" && "Active master policy"}
                      {masterPolicy.status === "quoted" && "Quote ready — needs your approval"}
                      {masterPolicy.status === "awaiting_payment" && `${masterPolicy.installments_remaining || 0} installments remaining`}
                    </div>
                    <div className="text-foreground/55 mt-0.5">
                      {masterPolicy.quote_insurer || "Allianz Trade"} ·
                      aggregate ${(masterPolicy.quote_coverage_limit || 0).toLocaleString()} · premium ${(masterPolicy.quote_premium_total || 0).toLocaleString()}/yr
                    </div>
                  </div>
                  <Link to="/seller/insurance" className="text-[11.5px] underline text-foreground/65 hover:text-foreground" data-testid="buyer-drawer-policy-link">
                    Manage →
                  </Link>
                </div>
              )}
              {tab === "overview" && <OverviewTab data={data} onCoverUpdate={() => {
                api.get(`/seller/buyers/${data.buyer.id}/detail`).then(r => setData(r.data)).catch(() => {});
              }}/>}
              {tab === "orders" && <OrdersTab orders={data.orders}/>}
              {tab === "invoices" && <InvoicesTab invoices={data.invoices}/>}
              {tab === "activity" && <ActivityTab timeline={data.timeline}/>}
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function MiniStat({ label, value, tone }) {
  const toneClass = tone === "warn" ? "text-amber-700" : "";
  return (
    <div className="rounded-xl border border-border bg-white p-3">
      <div className="text-[9px] mono uppercase tracking-wider text-foreground/55">{label}</div>
      <div className={`display text-sm mt-0.5 truncate ${toneClass}`}>{value}</div>
    </div>
  );
}

// v5.2 signal labels + weights — mirrors BuyerScore.jsx + AdminBuyers.jsx
// so sellers see the same engine taxonomy buyers and admins see.
// Imports live at the top of the file; aliased locals retained for
// backwards-compat with the existing JSX rendering below.

// Decision recommendation derived from the v3 engine output — gives the seller
// a one-line "approve / extend / hold" verdict so they don't have to interpret
// the signals manually.
function decisionFor(score) {
  if (!score) return { verdict: "Insufficient data", tone: "neutral", terms: "—",
                       reason: "No score available yet — request a credit application or wait for first orders." };
  const caps = score.caps_applied || [];
  if (caps.length > 0) {
    return {
      verdict: "Hold / restrict terms",
      tone: "rose",
      terms: "Wallet only",
      reason: `Buyer is hard-capped by the engine — ${caps[0]}. Recommend prepay / wallet-only until cap clears.`,
    };
  }
  const s = score.score || 0;
  if (s >= 780) return { verdict: "Approve · Premium", tone: "emerald", terms: "Net-60",
                          reason: `Excellent tier (${s}). Safe to extend Net-60 with limit up to $${(score.credit_limit||0).toLocaleString()}.` };
  if (s >= 740) return { verdict: "Approve · Strong", tone: "emerald", terms: "Net-30",
                          reason: `Very Good (${s}). Standard Net-30 recommended; limit up to $${(score.credit_limit||0).toLocaleString()}.` };
  if (s >= 700) return { verdict: "Approve · Standard", tone: "emerald", terms: "Net-30",
                          reason: `Good tier (${s}). Net-30 within the engine's recommended limit.` };
  if (s >= 640) return { verdict: "Approve cautiously", tone: "amber", terms: "Net-14",
                          reason: `Fair (${s}). Start with Net-14 + a tighter limit and re-evaluate after 3 paid invoices.` };
  if (s >= 580) return { verdict: "Limit exposure",   tone: "amber", terms: "Net-7 / Prepay",
                          reason: `Building (${s}). Consider prepay or a small Net-7 trial — monitor first orders closely.` };
  return            { verdict: "Decline / Prepay",   tone: "rose",  terms: "Wallet only",
                          reason: `Limited history (${s}). Wallet-only is the data-anchored peer reference — your underwriting policy applies.` };
}

const SIG_TIER_COLOR = (v) =>
  v >= 75 ? "bg-emerald-600"
    : v >= 50 ? "bg-foreground"
      : v >= 25 ? "bg-amber-500"
        : "bg-rose-500";

function OverviewTab({ data, onCoverUpdate }) {
  const score = data.score || {};
  const signals = score.signals || {};
  const bd = score.breakdown || {};
  const caps = score.caps_applied || [];
  const bonuses = score.bonuses_applied || [];
  const decision = decisionFor(score);

  const ratingLabel = normalizeRating(score.rating);
  const ratingExplainer = score.tier_explainer || SCORE_TIER_EXPLAINERS[ratingLabel] || "";
  const disclaimer = score.disclaimer || SCORE_DISCLAIMER;
  const referenceTerms = score.tier_band_reference_terms || score.default_terms;

  const ratingTone = ratingLabel === "Excellent" || ratingLabel === "Very Good" ? "text-emerald-600"
    : ratingLabel === "Good" ? "text-foreground"
      : ratingLabel === "Fair" ? "text-amber-600"
        : ratingLabel === "Building" ? "text-orange-600"
          : "text-slate-300";

  return (
    <div className="space-y-5" data-testid="seller-buyer-overview">
      {/* v5.2 · Neutral disclaimer — verqo provides data, not credit decisions */}
      <div
        className="rounded-xl border border-amber-200 bg-amber-50 text-amber-900 px-4 py-2.5 text-[12px] leading-relaxed flex items-start gap-2"
        data-testid="seller-buyer-score-disclaimer"
        role="note"
      >
        <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-amber-700"/>
        <div>
          <span className="font-semibold">Informational only.</span>{" "}
          {disclaimer}
        </div>
      </div>

      {/* ── Big score card · headline ─────────────────────────────────── */}
      <div className="rounded-2xl border border-border bg-foreground text-background p-5 relative overflow-hidden">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-[10px] mono uppercase tracking-[0.18em] text-background/55">verqo Trade Score v5.2</div>
            <div className="display text-5xl mt-1.5 leading-none" data-testid="seller-buyer-score">
              {score.score ?? "—"}
            </div>
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className={`inline-flex items-center gap-1 text-sm hover:opacity-80 transition-opacity ${ratingTone === "text-foreground" ? "text-background" : ratingTone}`}
                    data-testid="seller-buyer-score-tier"
                    aria-label={`Tier ${ratingLabel || "no rating"} — explanation`}
                  >
                    {ratingLabel || "no rating"}
                    <Info className="w-3 h-3 opacity-70"/>
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  side="bottom"
                  align="start"
                  className="max-w-xs text-[12.5px] leading-relaxed"
                  data-testid="seller-buyer-score-tier-popover"
                >
                  <div className="font-medium mb-1">{ratingLabel || "no rating"}</div>
                  <div className="text-foreground/70">{ratingExplainer || "No description available for this tier."}</div>
                  <div className="text-[11px] text-foreground/45 mt-2">
                    Description of observed data — not a credit recommendation.
                  </div>
                </PopoverContent>
              </Popover>
              {score.percentile != null && (
                <span
                  className="text-[10.5px] mono px-1.5 py-0.5 rounded-full bg-background/10 border border-background/20 text-background/85"
                  data-testid="seller-buyer-score-percentile"
                  title={`Percentile vs ${score.cohort_size || 0} peer buyers on verqo`}
                >
                  Top {Math.max(1, 100 - score.percentile)}%
                </span>
              )}
            </div>
          </div>
          <div className="text-right text-[11px] space-y-1.5 text-background/75">
            <div><span className="text-background/55">Tier-band reference limit · </span><span className="display text-base text-background">{money(score.credit_limit || 0)}</span></div>
            <div><span className="text-background/55">Reference rate · </span><span className="mono">{((score.rate || 0) * 100).toFixed(2)}%</span></div>
            <div><span className="text-background/55">Tier-band reference terms · </span><span className="font-medium">{TERMS_META[referenceTerms]?.label || referenceTerms || "—"}</span></div>
          </div>
        </div>
      </div>

      {/* ── Phase SC-5 · Allianz Cover Passport ────────────────────────── */}
      <AllianzCoverPanel
        cover={data.allianz_cover}
        buyerId={data.buyer?.id}
        buyerName={data.buyer?.company || data.buyer?.name}
        onUpdate={onCoverUpdate}
      />

      {/* ── Decision banner — the actionable "what should I do?" line ───── */}
      <div className={`rounded-xl border p-4 ${
        decision.tone === "emerald" ? "bg-emerald-50 border-emerald-200" :
        decision.tone === "amber"   ? "bg-amber-50 border-amber-200" :
        decision.tone === "rose"    ? "bg-rose-50 border-rose-200" :
        "bg-secondary border-border"
      }`} data-testid="seller-buyer-decision">
        <div className="flex items-center justify-between gap-3 mb-1.5">
          <div className={`text-[11px] mono uppercase tracking-[0.18em] ${
            decision.tone === "emerald" ? "text-emerald-700" :
            decision.tone === "amber"   ? "text-amber-700" :
            decision.tone === "rose"    ? "text-rose-700" :
            "text-foreground/60"
          }`}>Engine recommendation</div>
          <span className={`chip ${
            decision.tone === "emerald" ? "bg-emerald-100 text-emerald-800 border-emerald-300" :
            decision.tone === "amber"   ? "bg-amber-100 text-amber-800 border-amber-300" :
            decision.tone === "rose"    ? "bg-rose-100 text-rose-800 border-rose-300" :
            "bg-secondary text-foreground/70 border-border"
          } border`}>{decision.verdict} · {decision.terms}</span>
        </div>
        <div className={`text-xs leading-relaxed ${
          decision.tone === "emerald" ? "text-emerald-900" :
          decision.tone === "amber"   ? "text-amber-900" :
          decision.tone === "rose"    ? "text-rose-900" :
          "text-foreground/75"
        }`}>{decision.reason}</div>
      </div>

      {/* ── Hard caps + bonuses chips (always visible when present) ───── */}
      {(caps.length > 0 || bonuses.length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="seller-buyer-caps-bonuses">
          {caps.length > 0 && (
            <div className="rounded-xl border border-rose-200 bg-rose-50 p-3">
              <div className="text-[10px] mono uppercase tracking-[0.18em] text-rose-700 mb-1.5 flex items-center gap-1.5">
                <AlertTriangle className="w-3 h-3"/>Hard caps applied
              </div>
              <ul className="text-[11.5px] text-rose-900 space-y-1">
                {caps.map((c, i) => <li key={i}>• {c}</li>)}
              </ul>
            </div>
          )}
          {bonuses.length > 0 && (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3">
              <div className="text-[10px] mono uppercase tracking-[0.18em] text-emerald-700 mb-1.5 flex items-center gap-1.5">
                <ShieldCheck className="w-3 h-3"/>Bonuses applied
              </div>
              <ul className="text-[11.5px] text-emerald-900 space-y-1">
                {bonuses.map((b, i) => <li key={i}>• {b}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* ── v5.2 · 10 weighted buckets (Stage-2 score) ────────────────── */}
      <div>
        <div className="text-[10px] mono uppercase tracking-[0.18em] text-foreground/55 mb-3 flex items-center justify-between">
          <span>Signal breakdown · 10 weighted buckets · v5.2</span>
          {score.weights && (
            <span className="text-foreground/40">
              Σ {Math.round(Object.values(score.weights).reduce((a, b) => a + b, 0) * 100)}%
            </span>
          )}
        </div>
        <div className="space-y-2" data-testid="seller-buyer-signals">
          {SCORE_WEIGHTED_KEYS.filter(k => signals[k] !== undefined).map(k => {
            const v = signals[k];
            // Prefer LIVE weight from API (backend WEIGHTS dict); fall back
            // to v5.2 defaults if the server response is older.
            const wPct = score.weights?.[k] != null
              ? Math.round(score.weights[k] * 100)
              : (V3_SIGNAL_WEIGHT[k] ?? 0);
            return (
              <div key={k} className="flex items-center gap-3" data-testid={`sb-sig-${k}`}>
                <div className="text-xs text-foreground/70 w-44 shrink-0">
                  {V3_SIGNAL_LABEL[k] || k.replace(/_/g, " ")}
                  <span className="text-foreground/40 ml-1">· {wPct}%</span>
                </div>
                <div className="flex-1 h-1.5 bg-secondary rounded-full overflow-hidden">
                  <div className={`h-full ${SIG_TIER_COLOR(v)}`} style={{ width: `${v}%` }}/>
                </div>
                <div className="mono text-xs text-foreground/60 w-8 text-right">{v}</div>
              </div>
            );
          })}
          {Object.keys(signals).length === 0 && (
            <div className="text-xs text-foreground/45">Score signals not available yet — buyer may be too new.</div>
          )}
        </div>

        {/* v5.2 · Stage-1 Eligibility Gate (separate from scored buckets) */}
        {score.eligibility_gate && (
          <div className="mt-4 rounded-xl border border-border bg-secondary/30 p-3" data-testid="sb-eligibility-gate">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] mono uppercase tracking-[0.18em] text-foreground/55">
                Stage-1 Eligibility · KYB · OFAC · Bankruptcy
              </div>
              <span className={`text-[9px] mono px-1.5 py-0.5 rounded-full ${
                score.eligibility_gate.eligible
                  ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                  : "bg-amber-50 text-amber-800 border border-amber-200"
              }`}>
                {score.eligibility_gate.eligible ? "PASSING" : "GATE FAIL"}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-1.5 text-[11px]">
              {(score.eligibility_gate.gates || []).map(g => (
                <div key={g.key} className="flex items-center gap-1.5">
                  <span className={`mono text-[10px] ${
                    g.ok ? "text-emerald-700" : (g.kind === "hard" ? "text-rose-700" : "text-amber-700")
                  }`}>{g.ok ? "✓" : "✗"}</span>
                  <span className="text-foreground/70 capitalize">{g.key.replace(/_/g, " ")}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* v5.2 · Guardrails applied */}
        {score.guardrails_applied?.length > 0 && (
          <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-3" data-testid="sb-guardrails">
            <div className="text-[10px] mono uppercase tracking-[0.18em] text-amber-700 mb-1.5">
              Guardrails applied
            </div>
            <ul className="text-[11.5px] text-amber-900 space-y-1">
              {score.guardrails_applied.map((g, i) => <li key={i}>• {g}</li>)}
            </ul>
          </div>
        )}
      </div>

      {/* ── Key credit facts: utilization · default events · bureau ─── */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="seller-buyer-credit-facts">
        {/* Credit utilization */}
        <div className="rounded-xl bg-secondary/40 border border-border p-3">
          <div className="text-[10px] mono uppercase tracking-[0.18em] text-foreground/55">Credit utilization</div>
          <div className={`display text-xl mt-1 ${
            bd.credit_utilization?.utilization_band === "maxed_out" ? "text-rose-700" :
            bd.credit_utilization?.utilization_band === "stretched" ? "text-amber-700" :
            "text-foreground"
          }`}>
            {bd.credit_utilization?.utilization_pct != null ? `${bd.credit_utilization.utilization_pct}%` : "—"}
          </div>
          <div className="text-[10px] text-foreground/55 mt-0.5 capitalize">{(bd.credit_utilization?.utilization_band || "no data").replace(/_/g, " ")}</div>
          <div className="mt-2 space-y-0.5 text-[11px]">
            <div className="flex justify-between"><span className="text-foreground/60">Approved</span><span className="mono">{money(bd.credit_utilization?.approved_limit || 0)}</span></div>
            <div className="flex justify-between"><span className="text-foreground/60">Outstanding</span><span className="mono">{money(bd.credit_utilization?.outstanding_on_credit || 0)}</span></div>
          </div>
        </div>

        {/* Default events */}
        <div className="rounded-xl bg-secondary/40 border border-border p-3">
          <div className="text-[10px] mono uppercase tracking-[0.18em] text-foreground/55">Default events</div>
          <div className={`display text-xl mt-1 ${
            ((bd.default_events?.defaults_90d_plus_24mo || 0)
             + (bd.default_events?.adverse_actions_6mo || 0)
             + (bd.default_events?.open_claims_over_30d || 0)
             + (bd.default_events?.fraud_rejected_returns || 0)) > 0
            ? "text-rose-700" : "text-emerald-700"
          }`}>
            {(bd.default_events?.defaults_90d_plus_24mo || 0)
             + (bd.default_events?.adverse_actions_6mo || 0)
             + (bd.default_events?.open_claims_over_30d || 0)
             + (bd.default_events?.fraud_rejected_returns || 0)}
          </div>
          <div className="text-[10px] text-foreground/55 mt-0.5">total flags in scoring window</div>
          <div className="mt-2 space-y-0.5 text-[11px]">
            <div className="flex justify-between"><span className="text-foreground/60">Defaults 90+ days (24mo)</span><span className="mono text-rose-700">{bd.default_events?.defaults_90d_plus_24mo || 0}</span></div>
            <div className="flex justify-between"><span className="text-foreground/60">Open claims &gt;30d</span><span className="mono">{bd.default_events?.open_claims_over_30d || 0}</span></div>
            <div className="flex justify-between"><span className="text-foreground/60">Adverse actions</span><span className="mono">{bd.default_events?.adverse_actions_6mo || 0}</span></div>
            <div className="flex justify-between"><span className="text-foreground/60">Fraud returns</span><span className="mono">{bd.default_events?.fraud_rejected_returns || 0}</span></div>
          </div>
        </div>

        {/* KYB & cashflow */}
        <div className="rounded-xl bg-secondary/40 border border-border p-3">
          <div className="text-[10px] mono uppercase tracking-[0.18em] text-foreground/55">KYB · Cashflow</div>
          <div className="display text-xl mt-1">
            {bd.kyb?.kyb_status === "approved" ? "✓" : "—"}
            <span className="text-foreground/40 text-base ml-1">/ {bd.cashflow?.months_of_data || 0}mo</span>
          </div>
          <div className="text-[10px] text-foreground/55 mt-0.5">KYB / Plaid history</div>
          <div className="mt-2 space-y-0.5 text-[11px]">
            <div className="flex justify-between"><span className="text-foreground/60">TIN match</span><span className="mono">{bd.kyb?.tin_match ? "✓" : "—"}</span></div>
            <div className="flex justify-between"><span className="text-foreground/60">SOS active</span><span className="mono">{bd.kyb?.sos_active ? "✓" : "—"}</span></div>
            <div className="flex justify-between"><span className="text-foreground/60">NSF events</span><span className="mono">{bd.cashflow?.nsf_count || 0}</span></div>
            <div className="flex justify-between"><span className="text-foreground/60">Avg revenue</span><span className="mono">{money(bd.cashflow?.avg_monthly_revenue || 0)}</span></div>
          </div>
        </div>
      </div>

      {/* ── Your relationship with this buyer (seller-specific) ────────── */}
      <div>
        <div className="text-[10px] mono uppercase tracking-[0.18em] text-foreground/55 mb-3">
          Your relationship with this buyer
        </div>
        <div className="grid grid-cols-2 gap-2">
          <SummaryStat label="Orders" value={data.stats.orders_count}/>
          <SummaryStat label="Invoices" value={data.stats.invoices_count}/>
          <SummaryStat label="On-time rate" value={`${data.stats.on_time_pct}%`}/>
          <SummaryStat label="Joined" value={data.buyer.created_at ? new Date(data.buyer.created_at).toLocaleDateString() : "—"}/>
        </div>
      </div>

      {/* ── Policy history (preserved) ──────────────────────────────────── */}
      <div>
        <div className="text-[10px] mono uppercase tracking-[0.18em] text-foreground/55 mb-3">Policy history</div>
        {data.offers.length === 0 ? (
          <div className="text-xs text-foreground/45">No policies ever issued.</div>
        ) : (
          <div className="space-y-2" data-testid="policy-history">
            {data.offers.map(o => (
              <div key={o.id} className="rounded-lg border border-border bg-white px-3 py-2.5 flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 min-w-0">
                  <PolicyBadge offer={o}/>
                  <span className={`text-[10px] mono uppercase tracking-wider ${o.status === "active" ? "text-emerald-700" : "text-foreground/45"}`}>{o.status}</span>
                </div>
                <div className="text-[10px] mono text-foreground/50 shrink-0">
                  {new Date(o.created_at).toLocaleDateString()}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Phase 2b — Per-buyer shipping responsibility override.
       *  Lets the seller flip this specific buyer to seller-arranges or
       *  buyer-arranges regardless of the tenant default. Used when one
       *  retailer always uses their own UPS contract, another always
       *  wants you to ship — without forcing a global setting change. */}
      <BuyerShippingOverridePanel buyerId={data.buyer.id} />
    </div>
  );
}

function SummaryStat({ label, value }) {
  return (
    <div className="rounded-lg bg-secondary/30 border border-border px-3 py-2.5">
      <div className="text-[9px] mono uppercase tracking-wider text-foreground/55">{label}</div>
      <div className="font-medium mt-0.5 text-sm">{value}</div>
    </div>
  );
}

function OrdersTab({ orders }) {
  if (orders.length === 0) return <EmptyBlock icon={ShoppingBag} label="No orders yet"/>;
  return (
    <div className="space-y-2" data-testid="drawer-orders">
      {orders.map(o => (
        <div key={o.id} className="rounded-lg border border-border bg-white px-4 py-3 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-sm font-medium truncate">{o.code}</div>
            <div className="text-[11px] text-foreground/55 mt-0.5">
              {(o.items || []).length} item(s) · {new Date(o.created_at).toLocaleDateString()}
            </div>
          </div>
          <div className="text-right shrink-0">
            <div className="display text-base">{money(o.total || 0)}</div>
            <div className="text-[10px] mono uppercase tracking-wider text-foreground/55">{o.status}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

function InvoicesTab({ invoices }) {
  if (invoices.length === 0) return <EmptyBlock icon={Receipt} label="No invoices yet"/>;
  return (
    <div className="space-y-2" data-testid="drawer-invoices">
      {invoices.map(i => {
        const paid = i.status === "paid";
        const overdue = !paid && i.due_at && new Date(i.due_at) < new Date();
        return (
          <div key={i.id} className="rounded-lg border border-border bg-white px-4 py-3 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="text-sm font-medium truncate">{i.code}</div>
              <div className="text-[11px] text-foreground/55 mt-0.5">
                {(i.terms || "net30").toUpperCase()} · due {i.due_at ? new Date(i.due_at).toLocaleDateString() : "—"}
              </div>
            </div>
            <div className="text-right shrink-0">
              <div className="display text-base">{money(i.amount || 0)}</div>
              <div className={`text-[10px] mono uppercase tracking-wider ${paid ? "text-emerald-700" : overdue ? "text-rose-700" : "text-amber-700"}`}>
                {paid ? "paid" : overdue ? "overdue" : i.status}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ActivityTab({ timeline }) {
  if (!timeline || timeline.length === 0) return <EmptyBlock icon={Activity} label="No activity yet"/>;
  return (
    <div className="space-y-3 border-l-2 border-border pl-5" data-testid="drawer-activity">
      {timeline.map((e, i) => (
        <div key={i} className="relative">
          <div className="absolute -left-[26px] w-3 h-3 rounded-full bg-foreground/60 ring-4 ring-background"/>
          <div className="text-[12.5px] font-medium">{e.title}</div>
          {e.body && <div className="text-[11px] text-foreground/55 mt-0.5">{e.body}</div>}
          <div className="text-[9.5px] mono uppercase tracking-wider text-foreground/45 mt-1">
            {e.kind} · {e.at ? new Date(e.at).toLocaleString() : "—"}
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptyBlock({ icon: Icon, label }) {
  return (
    <div className="rounded-xl border border-dashed border-border bg-secondary/20 p-10 text-center">
      <Icon className="w-6 h-6 text-foreground/30 mx-auto"/>
      <div className="text-sm text-foreground/55 mt-2">{label}</div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// InsuranceSection — surfaces Allianz cover state inside the buyer drawer.
// ─────────────────────────────────────────────────────────────────────────────
function InsuranceSection({ insurance, onRequest }) {
  // Status meta inline so we don't pull from the insurance page.
  const meta = (() => {
    if (!insurance || insurance.source === "none") return { tone: "secondary", label: "Not covered", color: "text-foreground/60" };
    if (insurance.source === "allianz")             return { tone: "emerald",  label: "Covered · Allianz", color: "text-emerald-700" };
    if (insurance.source === "two_inc")             return { tone: "blue",     label: "Covered · Two.inc", color: "text-blue-700" };
    if (insurance.source === "investigating")       return { tone: "amber",    label: "Investigating",     color: "text-amber-700" };
    if (insurance.source === "refused")             return { tone: "rose",     label: "Refused",           color: "text-rose-700" };
    return { tone: "secondary", label: "—", color: "" };
  })();

  return (
    <div className="mt-4 rounded-xl border border-border bg-white px-4 py-3 flex items-center justify-between gap-3" data-testid="drawer-insurance">
      <div className="min-w-0 flex items-center gap-2">
        <ShieldCheck className={`w-4 h-4 shrink-0 ${meta.color || "text-foreground/60"}`}/>
        <div className="min-w-0">
          <div className="text-[12px] font-medium">Insurance · {meta.label}</div>
          {insurance?.source === "allianz" && (
            <div className="text-[11px] text-foreground/55 mt-0.5">
              {money(insurance.limit)} limit · {money(insurance.used)} used
            </div>
          )}
          {insurance?.source === "two_inc" && (
            <div className="text-[11px] text-foreground/55 mt-0.5">
              {insurance.two_inc_invoice_count} invoice(s) financed by Two.inc
            </div>
          )}
          {insurance?.source === "refused" && insurance.cover?.refusal_reason && (
            <div className="text-[11px] text-rose-700 mt-0.5 truncate max-w-[280px]">{insurance.cover.refusal_reason}</div>
          )}
          {insurance?.source === "investigating" && insurance.cover?.investigation_eta && (
            <div className="text-[11px] text-amber-700 mt-0.5">ETA {new Date(insurance.cover.investigation_eta).toLocaleDateString()}</div>
          )}
        </div>
      </div>
      {(insurance?.source === "none" || insurance?.source === "refused") && (
        <Button size="sm" onClick={onRequest} data-testid="drawer-insure" className="rounded-full text-xs h-8 px-3">
          {insurance?.source === "refused" ? "Re-request" : "Insure"}
        </Button>
      )}
      {insurance?.source === "allianz" && (
        <Button size="sm" variant="outline" onClick={onRequest} className="rounded-full text-xs h-8 px-3">
          Manage
        </Button>
      )}
    </div>
  );
}


// ─── Tags editor + Statement download (per-buyer) ─────────────────────
function BuyerTagsAndStatement({ buyerId }) {
  const [tags, setTags] = useState([]);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [period, setPeriod] = useState(90);

  useEffect(() => {
    if (!buyerId) return;
    api.get("/seller/buyers/tags").then(r => {
      setTags((r.data?.by_buyer || {})[buyerId] || []);
    }).catch(() => setTags([]));
  }, [buyerId]);

  const save = async (next) => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/seller/buyers/${buyerId}/tags`, { tags: next });
      setTags(data.tags || next);
    } catch (e) { toast.error(e.response?.data?.detail || "Tag save failed"); }
    finally { setBusy(false); }
  };

  const addTag = () => {
    const t = draft.trim();
    if (!t) return;
    if (tags.includes(t)) { setDraft(""); return; }
    const next = [...tags, t];
    setDraft(""); save(next);
  };
  const removeTag = (t) => save(tags.filter(x => x !== t));

  const downloadStatement = async () => {
    setDownloading(true);
    try {
      const r = await api.get(`/seller/buyers/${buyerId}/statement?period_days=${period}`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `statement-${buyerId.slice(0, 8)}-${period}d.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success(`Statement (${period} days) downloaded`);
    } catch (e) { toast.error("Statement failed"); }
    finally { setDownloading(false); }
  };

  const emailStatement = async () => {
    setDownloading(true);
    try {
      const { data } = await api.post(`/seller/buyers/${buyerId}/statement/email`, { period_days: period, cc_self: true });
      toast.success(`Statement emailed to ${data.to}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Email failed (Resend not configured?)"); }
    finally { setDownloading(false); }
  };

  return (
    <div className="px-6 py-4 border-b border-border bg-foreground/[0.02]" data-testid="buyer-tags-statement">
      <div className="flex items-start gap-4 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-wider text-foreground/55 mb-1.5">Private tags · only you see these</div>
          <div className="flex flex-wrap gap-1.5 items-center">
            {tags.map(t => (
              <span key={t} data-testid={`tag-${t}`} className="inline-flex items-center gap-1 text-[11px] bg-foreground text-background px-2 py-0.5 rounded-full">
                {t}
                <button onClick={() => removeTag(t)} disabled={busy} className="text-background/70 hover:text-background">
                  <X className="w-2.5 h-2.5"/>
                </button>
              </span>
            ))}
            <Input
              value={draft}
              onChange={e => setDraft(e.target.value)}
              onKeyDown={e => e.key === "Enter" && (e.preventDefault(), addTag())}
              onBlur={addTag}
              placeholder="Add tag…"
              className="h-7 text-xs w-32"
              data-testid="tag-input"
            />
          </div>
        </div>
        <div className="shrink-0">
          <div className="text-[10px] uppercase tracking-wider text-foreground/55 mb-1.5">Statement</div>
          <div className="flex items-center gap-1">
            <select value={period} onChange={e => setPeriod(Number(e.target.value))}
              className="h-8 text-xs rounded border border-border bg-white px-2" data-testid="stmt-period">
              <option value={30}>30d</option>
              <option value={60}>60d</option>
              <option value={90}>90d</option>
              <option value={180}>180d</option>
              <option value={365}>365d</option>
            </select>
            <Button size="sm" variant="outline" onClick={downloadStatement} disabled={downloading} className="h-8 text-xs rounded-full" data-testid="stmt-download">
              <Download className="w-3 h-3 mr-1"/>PDF
            </Button>
            <Button size="sm" variant="outline" onClick={emailStatement} disabled={downloading} className="h-8 text-xs rounded-full" data-testid="stmt-email">
              <Mail className="w-3 h-3 mr-1"/>Email
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}



// ─── AutoFundQuickToggle — Phase SC-2 inline buyer-drawer widget ─────────────
// Lets the seller add/remove this specific buyer to auto-fund without leaving
// the drawer. If a rule exists, shows status + a quick disable/resume. If not,
// shows a CTA opening a 3-field setup form.
function AutoFundQuickToggle({ buyerId, buyerCompany }) {
  const [loading, setLoading] = useState(true);
  const [rule, setRule] = useState(null);
  const [setupOpen, setSetupOpen] = useState(false);
  const [minInvoice, setMinInvoice] = useState("");
  const [dailyCap, setDailyCap] = useState("");
  const [monthlyCap, setMonthlyCap] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/seller/auto-fund/rules/by-buyer/${buyerId}`);
      setRule(r.data.rule);
    } catch {
      setRule(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!buyerId) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buyerId]);

  const toggle = async () => {
    if (!rule) return;
    try {
      await api.patch(`/seller/auto-fund/rules/${rule.id}`, { enabled: !rule.enabled });
      toast.success(rule.enabled ? "Auto-fund paused" : "Auto-fund enabled");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || "Update failed");
    }
  };

  const resume = async () => {
    try {
      await api.post(`/seller/auto-fund/rules/${rule.id}/resume`);
      toast.success("Auto-fund resumed");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || "Resume failed");
    }
  };

  const remove = async () => {
    if (!window.confirm("Remove auto-fund rule for this buyer?")) return;
    try {
      await api.delete(`/seller/auto-fund/rules/${rule.id}`);
      toast.success("Rule removed");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || "Delete failed");
    }
  };

  const submitNew = async () => {
    if (!minInvoice || !dailyCap || !monthlyCap) return toast.error("Fill every field");
    if (Number(dailyCap) > Number(monthlyCap)) return toast.error("Daily cap can't exceed monthly cap");
    setSubmitting(true);
    try {
      await api.post("/seller/auto-fund/rules", {
        buyer_id:        buyerId,
        enabled:         true,
        min_invoice_usd: Number(minInvoice),
        daily_cap_usd:   Number(dailyCap),
        monthly_cap_usd: Number(monthlyCap),
      });
      toast.success("Auto-fund enabled");
      setSetupOpen(false);
      setMinInvoice(""); setDailyCap(""); setMonthlyCap("");
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(d?.message || d?.reason || "Could not create rule");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="mt-4 rounded-xl border border-border bg-white px-4 py-3 text-[12px] text-foreground/55" data-testid="drawer-auto-fund-loading">
        Loading auto-fund…
      </div>
    );
  }

  return (
    <div className="mt-4 rounded-xl border border-border bg-white px-4 py-3" data-testid="drawer-auto-fund">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0 flex items-center gap-2">
          <Sparkles className={`w-4 h-4 shrink-0 ${rule ? (rule.paused_due_to_dispute ? "text-amber-600" : rule.enabled ? "text-violet-600" : "text-foreground/40") : "text-foreground/40"}`}/>
          <div className="min-w-0">
            <div className="text-[12px] font-medium">
              Auto-fund · {rule ? (rule.paused_due_to_dispute ? "Paused (dispute)" : rule.enabled ? "Active" : "Paused") : "Not enabled"}
            </div>
            {rule && (
              <div className="text-[11px] text-foreground/55 mt-0.5">
                Min {money(rule.min_invoice_usd)} · Daily cap {money(rule.daily_cap_usd)} · Monthly {money(rule.monthly_cap_usd)}
              </div>
            )}
            {!rule && (
              <div className="text-[11px] text-foreground/55 mt-0.5">
                Auto-purchase every new Net-X invoice from this buyer
              </div>
            )}
          </div>
        </div>
        <div className="shrink-0 flex gap-1.5">
          {rule ? (
            <>
              {rule.paused_due_to_dispute ? (
                <Button size="sm" onClick={resume} data-testid="drawer-auto-fund-resume" className="rounded-full text-xs h-8 px-3 bg-emerald-600 hover:bg-emerald-700 text-white">
                  Resume
                </Button>
              ) : (
                <Button size="sm" variant="outline" onClick={toggle} data-testid="drawer-auto-fund-toggle" className="rounded-full text-xs h-8 px-3">
                  {rule.enabled ? "Pause" : "Enable"}
                </Button>
              )}
              <button onClick={remove} data-testid="drawer-auto-fund-remove" className="w-8 h-8 grid place-items-center rounded-full hover:bg-rose-50 text-rose-600">
                <Trash2 className="w-3.5 h-3.5"/>
              </button>
            </>
          ) : (
            <Button size="sm" onClick={() => setSetupOpen(true)} data-testid="drawer-auto-fund-setup" className="rounded-full text-xs h-8 px-3 bg-violet-600 hover:bg-violet-700 text-white">
              Set up
            </Button>
          )}
        </div>
      </div>

      {setupOpen && (
        <div className="mt-3 pt-3 border-t border-border space-y-3" data-testid="drawer-auto-fund-form">
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label className="text-[10.5px]">Min invoice ($)</Label>
              <Input type="number" min={0} step={100} value={minInvoice}
                onChange={e => setMinInvoice(e.target.value)}
                data-testid="drawer-auto-fund-min"
                className="mt-1 h-9 text-[12px]"/>
            </div>
            <div>
              <Label className="text-[10.5px]">Daily cap ($)</Label>
              <Input type="number" min={1} step={1000} value={dailyCap}
                onChange={e => setDailyCap(e.target.value)}
                data-testid="drawer-auto-fund-daily"
                className="mt-1 h-9 text-[12px]"/>
            </div>
            <div>
              <Label className="text-[10.5px]">Monthly cap ($)</Label>
              <Input type="number" min={1} step={5000} value={monthlyCap}
                onChange={e => setMonthlyCap(e.target.value)}
                data-testid="drawer-auto-fund-monthly"
                className="mt-1 h-9 text-[12px]"/>
            </div>
          </div>
          <div className="text-[10.5px] text-foreground/55">
            We'll pause this rule automatically if any invoice from <span className="font-medium">{buyerCompany}</span> enters dispute.
          </div>
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="outline" onClick={() => setSetupOpen(false)} className="rounded-full text-xs h-8">Cancel</Button>
            <Button size="sm" onClick={submitNew} disabled={submitting} data-testid="drawer-auto-fund-submit" className="rounded-full text-xs h-8 bg-violet-600 hover:bg-violet-700 text-white">
              {submitting ? "Saving…" : "Enable auto-fund"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// BuyerShippingOverridePanel — Phase 2b. Per-buyer shipping responsibility
// override surfaced in the Buyer 360° drawer's Overview tab.
//
// Resolves to one of three states:
//   "default"          → use the tenant default (settings.profile.offer_seller_arranged_shipping)
//   "seller_arranges"  → this buyer always gets seller-arranged shipping
//   "buyer_arranges"   → this buyer always books their own carrier
//
// The "effective_mode" the API returns is the concrete decision (never
// `default`) so the UI can show the seller what the next order with this
// buyer will actually resolve to, without them having to mentally combine
// the override + tenant default.
// ─────────────────────────────────────────────────────────────────────────────
function BuyerShippingOverridePanel({ buyerId }) {
  const [policy, setPolicy] = useState(null);
  const [saving, setSaving] = useState(false);
  const [reason, setReason] = useState("");
  const [showReason, setShowReason] = useState(false);
  const [pendingValue, setPendingValue] = useState(null);

  const load = () => {
    api.get(`/seller/buyers/${buyerId}/shipping-policy`)
      .then(r => setPolicy(r.data))
      .catch(() => setPolicy(null));
  };
  useEffect(() => { if (buyerId) load(); }, [buyerId]); // eslint-disable-line react-hooks/exhaustive-deps

  const apply = async (value, reasonText) => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/seller/buyers/${buyerId}/shipping-policy`, {
        value, reason: reasonText || null,
      });
      setPolicy(p => ({ ...(p || {}), override: data.override, effective_mode: data.effective_mode }));
      toast.success(value === "default" ? "Override cleared" : `Set to ${labelForMode(value)}`);
      setShowReason(false);
      setReason("");
      setPendingValue(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Couldn't update");
    } finally { setSaving(false); }
  };

  const handlePick = (value) => {
    if (value === "default") return apply(value, null);
    // For non-default overrides, gently ask for a reason so the audit
    // log + future support questions have context. Not blocking.
    setPendingValue(value);
    setShowReason(true);
  };

  if (!policy) return null;
  const current = policy.override || "default";
  const effective = policy.effective_mode || "buyer_arranges";

  return (
    <div className="rounded-2xl border border-border bg-white p-5" data-testid="buyer-shipping-override-panel">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <Truck className="w-4 h-4 text-foreground/55" />
          <div>
            <div className="text-[13px] font-semibold tracking-tight">Shipping responsibility</div>
            <div className="text-[11.5px] text-foreground/55 leading-snug mt-0.5">
              Override the tenant default just for this buyer.
              Effective for the next order: <span className="font-medium text-foreground/85">{labelForMode(effective)}</span>
            </div>
          </div>
        </div>
        {current !== "default" && (
          <span className="px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider bg-amber-500/15 text-amber-700 border border-amber-500/30">
            Override active
          </span>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        {[
          { v: "default",          label: "Use tenant default" },
          { v: "seller_arranges",  label: "Seller arranges" },
          { v: "buyer_arranges",   label: "Buyer arranges" },
        ].map(opt => {
          const active = current === opt.v;
          return (
            <button
              key={opt.v}
              type="button"
              onClick={() => !saving && handlePick(opt.v)}
              disabled={saving}
              data-testid={`buyer-shipping-override-${opt.v}`}
              className={`px-3 py-1.5 rounded-full text-[12px] border transition-colors ${
                active
                  ? "bg-foreground text-background border-foreground"
                  : "bg-white text-foreground/75 border-foreground/15 hover:border-foreground/35"
              } disabled:opacity-50`}
            >
              {opt.label}
            </button>
          );
        })}
      </div>

      {showReason && pendingValue && (
        <div className="mt-3 rounded-lg border border-border bg-secondary/30 p-3 space-y-2" data-testid="buyer-shipping-override-reason">
          <div className="text-[11.5px] text-foreground/65">
            Adding a quick reason helps your team understand later (optional but recommended):
          </div>
          <Input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. They use their own UPS contract"
            className="text-[12.5px]"
            data-testid="buyer-shipping-override-reason-input"
          />
          <div className="flex gap-2 justify-end">
            <Button size="sm" variant="ghost" className="rounded-full"
                    onClick={() => { setShowReason(false); setReason(""); setPendingValue(null); }}
                    disabled={saving}>
              Cancel
            </Button>
            <Button size="sm" className="rounded-full bg-foreground text-background"
                    onClick={() => apply(pendingValue, reason)}
                    disabled={saving}
                    data-testid="buyer-shipping-override-confirm">
              {saving ? "Saving…" : `Set to ${labelForMode(pendingValue)}`}
            </Button>
          </div>
        </div>
      )}

      {policy.set_at && current !== "default" && (
        <div className="mt-3 text-[10.5px] text-foreground/45">
          Override set {new Date(policy.set_at).toLocaleDateString()}
          {policy.reason ? ` · "${policy.reason}"` : ""}
        </div>
      )}
    </div>
  );
}

function labelForMode(mode) {
  return mode === "seller_arranges" ? "Seller arranges"
       : mode === "buyer_arranges"  ? "Buyer arranges"
       : "Tenant default";
}



// ─────────────────────────────────────────────────────────────────────────────
// SampleBuyersPreview — Phase 1 of the empty-state sample preview pattern.
//
// Renders the EXACT same column layout as a real Buyers row, but with three
// hardcoded sample rows. Visually muted (60% opacity, slate text, no hover
// effects), explicitly labeled "SAMPLE" with a ⊘ no-action indicator instead
// of action buttons. ZERO database writes — these rows never touch
// db.connections, db.invites, or any other collection.
//
// Lifecycle:
//   - Renders when the seller has truly empty state (no rows, no search,
//     no filter, no facets). One conditional in SellerBuyers handles this.
//   - Disappears the moment the seller has their first real connection or
//     invite. Single-entity threshold (not 5 or 10) so there's no cliff.
//
// Why preview-only (not seeded data):
//   - No anchoring (fake $50K AR would mislead onboarding sellers).
//   - No accidental real actions (clicks are inert with a sample tooltip).
//   - No compliance risk (sponsor bank audits hate seeded fake invoices).
//   - No filter/sort/CSV pollution (real Mongo collections stay pure).
// ─────────────────────────────────────────────────────────────────────────────
import { SAMPLE_BUYERS as SHARED_SAMPLE_BUYERS } from "../../lib/sampleData";
import { SamplePreviewBanner, SampleChip, SampleBuyerDrawer } from "../../components/SamplePreview";

function SampleBuyersPreview({ onInvite }) {
  const [sampleBuyer, setSampleBuyer] = useState(null);
  return (
    <div data-testid="sample-buyers-preview">
      <SamplePreviewBanner
        noun="Buyers"
        cta="Invite your first buyer"
        ctaTestId="sample-buyers-invite-cta"
        onCta={onInvite}
      />
      <div className="divide-y divide-border">
        {SHARED_SAMPLE_BUYERS.map(s => (
          <SampleBuyerRow key={s.id} sample={s} onOpen={() => setSampleBuyer(s)} />
        ))}
      </div>
      <SampleBuyerDrawer buyer={sampleBuyer} onClose={() => setSampleBuyer(null)} />
    </div>
  );
}

function SampleBuyerRow({ sample, onOpen }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className="w-full text-left grid grid-cols-[2fr_120px_70px_120px_110px_140px_110px_140px] gap-4 px-5 py-3 items-center opacity-[0.78] hover:opacity-100 hover:bg-secondary/40 transition-all"
      data-testid={`sample-buyer-row-${sample.id}`}
      aria-label={`Sample row - open ${sample.name}`}
    >
      <div className="min-w-0">
        <div className="text-[13.5px] font-medium truncate flex items-center gap-1.5 text-slate-700">
          {sample.name}
          <SampleChip />
        </div>
        <div className="text-[11.5px] text-slate-500 truncate">{sample.email}</div>
      </div>
      <div>
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider ${
          sample.status === "connected"
            ? "bg-emerald-500/15 text-emerald-700 border border-emerald-500/20"
            : "bg-amber-500/15 text-amber-800 border border-amber-500/20"
        }`}>
          {sample.status === "connected" ? "Connected" : "Invited"}
        </span>
      </div>
      <div className="flex justify-center">
        <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-slate-100 text-slate-600 text-[11px] font-semibold mono border border-slate-200">
          {sample.verqo_score}
        </span>
      </div>
      <div>
        <span className="text-[11.5px] text-slate-400">{sample.rating}</span>
      </div>
      <div className="text-[13px] font-medium text-slate-600">
        {sample.open_ar ? `$${sample.open_ar.toLocaleString()}` : <span className="text-foreground/30">—</span>}
      </div>
      <div className="text-[12.5px] text-slate-600">{sample.terms}</div>
      <div className="text-[12.5px] text-slate-500">{sample.last_order_at}</div>
      <div className="flex justify-end text-foreground/40 text-[11px]">Open →</div>
    </button>
  );
}

